import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReturnToValidationBanner, { goBackToValidation } from '../common/ReturnToValidation';
import ServerUnavailable from '../common/ServerUnavailable';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableSortLabel,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Snackbar,
  Menu,
  ListItemIcon,
  ListItemText,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Assessment as AssessmentIcon,
  AccountTree as AccountTreeIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  CloudSync as CloudSyncIcon,
  MoreVert as MoreVertIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Send as SendIcon,
  CloudDownload as CloudDownloadIcon,
  ExpandMore as ExpandMoreIcon,
  FormatListBulleted as FormatListBulletedIcon
} from '@mui/icons-material';
import api from '../../config/axiosConfig';
import { FIELD_LIMITS } from '../../constants/fieldLimits';
import { capitalizePersonName } from '../../utils/textCase';
import CaseTypoHint from '../common/CaseTypoHint';
import { validatePostalCode, validateGeolocation } from '../utilerias/ValidationUtils';
import AddBusinessSectorModal from '../catalogos/AddBusinessSectorModal';
import UploadDocumentosDialog from '../servicios/UploadDocumentosDialog';
import { isJpgOrPng, JPG_PNG_ACCEPT } from '../../utils/imageFile';

// corporate_contact_title es varchar(5) en BD -- todas estas opciones caben en ese límite
// ("Srta." en vez de "Srita." por lo mismo). El campo sigue aceptando texto libre (Autocomplete
// freeSolo), esto solo precarga las más comunes para no escribirlas siempre a mano.
const COMMON_CONTACT_TITLES = ['Lic.', 'Ing.', 'Arq.', 'Dr.', 'Dra.', 'Mtro.', 'Mtra.', 'C.P.', 'Sr.', 'Sra.', 'Srta.'];

const MEXICO_STATES = [
  'Aguascalientes',
  'Baja California',
  'Baja California Sur',
  'Campeche',
  'Chiapas',
  'Chihuahua',
  'Ciudad de México',
  'Coahuila',
  'Colima',
  'Durango',
  'Estado de México',
  'Guanajuato',
  'Guerrero',
  'Hidalgo',
  'Jalisco',
  'Michoacán',
  'Morelos',
  'Nayarit',
  'Nuevo León',
  'Oaxaca',
  'Puebla',
  'Querétaro',
  'Quintana Roo',
  'San Luis Potosí',
  'Sinaloa',
  'Sonora',
  'Tabasco',
  'Tamaulipas',
  'Tlaxcala',
  'Veracruz',
  'Yucatán',
  'Zacatecas'
];

const CatalogoEmpresas = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Si llegó desde "Validar Campos" (Programas): al guardar regresa solo a la validación
  const [returnToValidation] = useState(() => location.state?.returnToValidation || null);
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Falla al cargar la pantalla: 'network' (backend abajo) | 'server' -> vista "Sin conexión"
  const [loadErrorKind, setLoadErrorKind] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    matriz: 0
  });

  // Opciones para los campos del perfil de riesgo
  const propertyTypes = [
    'Oficina', 'Almacén', 'Fábrica', 'Comercio', 'Restaurante', 
    'Hospital', 'Escuela', 'Hotel', 'Centro Comercial', 'Otro'
  ];

  const riskLevels = [
    { value: 'BAJO', label: 'Bajo', color: 'success' },
    { value: 'MEDIO', label: 'Medio', color: 'warning' },
    { value: 'ALTO', label: 'Alto', color: 'error' }
  ];

  const fireProtectionOptions = [
    'Extintores', 'Sistema de rociadores', 'Detectores de humo',
    'Alarmas contra incendios', 'Sistema de supresión', 'Hidrantes'
  ];

  const securityMeasuresOptions = [
    'Cámaras de seguridad', 'Control de acceso', 'Guardias de seguridad',
    'Sistemas de alarma', 'Cercas perimetrales', 'Iluminación de seguridad'
  ];
  
  // Estado para errores de validación
  const [validationError, setValidationError] = useState('');
  // Bloqueo de datos de la matriz mientras la empresa tenga una cotización enviada u OT abierta
  const [entityLock, setEntityLock] = useState(null);
  const [rfcValidation, setRfcValidation] = useState({ isValid: true, message: '', tipo: '' });
  const [emailValidation, setEmailValidation] = useState({ isValid: true, message: '' });
  const [postalCodeValidation, setPostalCodeValidation] = useState({ isValid: true, message: '' });
  const [fiscalPostalCodeValidation, setFiscalPostalCodeValidation] = useState({ isValid: true, message: '' });
  const [geolocationValidation, setGeolocationValidation] = useState({ isValid: true, message: '' });
  const [phoneValidation, setPhoneValidation] = useState({ isValid: true, message: '' });
  const [legalRepresentativeValidation, setLegalRepresentativeValidation] = useState({ isValid: true, message: '' });
  
  // Estado para errores de campos individuales (para mostrar bordes rojos)
  const [fieldErrors, setFieldErrors] = useState({});
  
  // Estado para creación de estructura Dropbox
  const [dropboxLoading, setDropboxLoading] = useState(false);
  const [dropboxSuccess, setDropboxSuccess] = useState('');
  const [dropboxWarning, setDropboxWarning] = useState('');
  
  // Estado para notificaciones
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Estado para reenvío de invitación al usuario invitado
  const [resendingInvitation, setResendingInvitation] = useState(false);

  const handleResendInvitation = async () => {
    if (!formData.id) return;
    setResendingInvitation(true);
    try {
      await api.post(`/companies/${formData.id}/resend-invitation`);
      setSnackbar({ open: true, message: 'Invitación en camino; llegará al cliente en unos minutos.', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error al reenviar la invitación',
        severity: 'error'
      });
    } finally {
      setResendingInvitation(false);
    }
  };
  
  // Estado para el menú de acciones
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedCompanyForAction, setSelectedCompanyForAction] = useState(null);

  // Estado para la información del usuario
  const [userInfo, setUserInfo] = useState(null);
  const isAdmin = userInfo?.role === 'ADMINISTRADOR';

  // Confirmación de eliminación permanente (solo admin)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  
  // Estado para sectores empresariales
  const [businessSectors, setBusinessSectors] = useState([]);
  const [sectorModalOpen, setSectorModalOpen] = useState(false);
  
  const [filters, setFilters] = useState({
    businessName: '',
    rfc: '',
    businessSector: '',
    companyType: '',
    isActive: ''
  });

  // Ordenamiento de la tabla (elegido por el usuario: Nombre Comercial, Razón Social o RFC)
  const [sortField, setSortField] = useState('businessName');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSortRequest = (field) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedCompanies = [...filteredCompanies].sort((a, b) => {
    const valueA = (a[sortField] || '').toString().toLowerCase();
    const valueB = (b[sortField] || '').toString().toLowerCase();
    if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
    if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Funciones para manejar el menú de acciones
  const handleActionMenuOpen = (event, company) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setSelectedCompanyForAction(company);
  };
  
  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedCompanyForAction(null);
  };
  
  const handleMenuAction = (action) => {
    if (!selectedCompanyForAction) return;
    
    switch(action) {
      case 'branches':
        handleViewBranches(selectedCompanyForAction);
        break;
      case 'dropbox':
        handleCreateDropboxStructure(selectedCompanyForAction);
        break;
      case 'documents':
        setSelectedCompanyForDocs(selectedCompanyForAction);
        setDocumentsDialogOpen(true);
        break;
      case 'edit':
        handleEditClick(selectedCompanyForAction);
        break;
      case 'delete':
        setCompanyToDelete(selectedCompanyForAction);
        setDeleteConfirmOpen(true);
        break;
      default:
        break;
    }
    
    handleActionMenuClose();
  };

  // Estado de expandido/colapsado de cada sección del diálogo de edición -- persistido en
  // sessionStorage para que se recuerde mientras dure la sesión (se pierde al cerrar la pestaña,
  // igual que el resto del estado de sesión de la app -- authToken/userInfo usan el mismo storage).
  const SECTION_STORAGE_KEY = 'catalogoEmpresas_expandedSections';
  const DEFAULT_EXPANDED_SECTIONS = {
    general: true,
    contacto: false,
    direccionFisica: false,
    direccionFiscal: false,
    datosInmueble: false,
    logo: false,
    fotoInmueble: false,
    constanciaFiscal: false,
  };
  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      const stored = sessionStorage.getItem(SECTION_STORAGE_KEY);
      if (stored) return { ...DEFAULT_EXPANDED_SECTIONS, ...JSON.parse(stored) };
    } catch { /* sessionStorage no disponible -- usar default */ }
    return DEFAULT_EXPANDED_SECTIONS;
  });
  const toggleSection = (key) => {
    setExpandedSections(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { sessionStorage.setItem(SECTION_STORAGE_KEY, JSON.stringify(next)); } catch { /* no-op */ }
      return next;
    });
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingHighlightFieldKey, setPendingHighlightFieldKey] = useState(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // Administradores sí pueden corregir; para los demás los campos bloqueados quedan deshabilitados
  const lockFields = !!(isEditMode && entityLock?.locked && !entityLock?.canOverride && !entityLock?.editableWithNotice);
  const [debounceTimer, setDebounceTimer] = useState(null);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedCompanyForDocs, setSelectedCompanyForDocs] = useState(null);

  // Estados para foto del inmueble (matriz)
  const [companyHasPropertyImage, setCompanyHasPropertyImage] = useState(false);
  const [propertyImageFile, setPropertyImageFile] = useState(null);
  const [propertyImagePreview, setPropertyImagePreview] = useState(null);
  const [propertyImageUploading, setPropertyImageUploading] = useState(false);
  const [propertyImageSuccess, setPropertyImageSuccess] = useState(false);
  const [propertyImageError, setPropertyImageError] = useState('');
  const [propertyImagePreviewLoading, setPropertyImagePreviewLoading] = useState(false);
  const propertyImageInputRef = useRef(null);

  const handlePropertyImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!isJpgOrPng(file)) {
      setPropertyImageError('Solo se permiten archivos .jpg o .png');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setPropertyImageError('El archivo no puede superar 8 MB');
      return;
    }
    setPropertyImageFile(file);
    setPropertyImagePreview(URL.createObjectURL(file));
    setPropertyImageError('');
  };

  const handlePropertyImageUpload = async () => {
    if (!propertyImageFile || !formData.id) return false;
    setPropertyImageUploading(true);
    setPropertyImageError('');
    try {
      const fd = new FormData();
      fd.append('file', propertyImageFile);
      await api.post(`/companies/${formData.id}/property-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPropertyImageSuccess(true);
      setPropertyImageFile(null);
      setCompanyHasPropertyImage(true);
      setTimeout(() => setPropertyImageSuccess(false), 4000);
      return true;
    } catch (err) {
      setPropertyImageError(err.response?.data?.error || 'Error al subir la fotografía. Intenta de nuevo.');
      return false;
    } finally {
      setPropertyImageUploading(false);
    }
  };

  const [companyHasLogo, setCompanyHasLogo] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoSuccess, setLogoSuccess] = useState(false);
  const [logoError, setLogoError] = useState('');
  const [logoPreviewLoading, setLogoPreviewLoading] = useState(false);
  const logoInputRef = useRef(null);

  // Trae la imagen YA guardada en Dropbox (logo o fotografía del inmueble) y la muestra como
  // preview real, en vez de solo el check "hay un archivo guardado". Si el backend dice que hay
  // archivo (hasLogo/hasPropertyImage) pero la descarga falla, es evidencia de que en realidad
  // no se guardó bien (o el archivo ya no existe en Dropbox) -- se lo marcamos como error visible.
  const loadSavedImagePreview = async (companyId, kind) => {
    const setPreview = kind === 'logo' ? setLogoPreview : setPropertyImagePreview;
    const setLoading = kind === 'logo' ? setLogoPreviewLoading : setPropertyImagePreviewLoading;
    const setErr = kind === 'logo' ? setLogoError : setPropertyImageError;
    setLoading(true);
    try {
      const response = await api.get(`/companies/${companyId}/${kind}`, { responseType: 'blob' });
      setPreview(URL.createObjectURL(response.data));
    } catch (err) {
      setPreview(null);
      setErr(kind === 'logo'
        ? 'El sistema indica que hay un logo guardado, pero no se pudo descargar desde Dropbox. Probablemente la subida no se completó correctamente.'
        : 'El sistema indica que hay una fotografía guardada, pero no se pudo descargar desde Dropbox. Probablemente la subida no se completó correctamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!isJpgOrPng(file)) {
      setLogoError('Solo se permiten archivos .jpg o .png');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setLogoError('El archivo no puede superar 5 MB');
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setLogoError('');
  };

  const handleLogoUpload = async () => {
    if (!logoFile || !formData.id) return false;
    setLogoUploading(true);
    setLogoError('');
    try {
      const fd = new FormData();
      fd.append('file', logoFile);
      await api.post(`/companies/${formData.id}/logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogoSuccess(true);
      setLogoFile(null);
      setCompanyHasLogo(true);
      setTimeout(() => setLogoSuccess(false), 4000);
      return true;
    } catch (err) {
      setLogoError(err.response?.data?.error || 'Error al subir el logo. Intenta de nuevo.');
      return false;
    } finally {
      setLogoUploading(false);
    }
  };

  // Constancia Fiscal -- a diferencia de logo/foto de inmueble, no es obligatoria y no tiene
  // preview inline (es un PDF, no una imagen); en su lugar, "ver" abre el PDF en una pestaña
  // nueva y "descargar" lo baja al equipo, ambos vía el endpoint genérico de Dropbox que ya usa
  // Subir Archivos (mismo patrón, path-based) -- no hace falta un endpoint nuevo.
  const [constanciaFile, setConstanciaFile] = useState(null);
  const [constanciaPreview, setConstanciaPreview] = useState(null);
  const [constanciaPreviewLoading, setConstanciaPreviewLoading] = useState(false);
  const [constanciaUploading, setConstanciaUploading] = useState(false);
  const [constanciaSuccess, setConstanciaSuccess] = useState(false);
  const [constanciaError, setConstanciaError] = useState('');
  const [constanciaActionLoading, setConstanciaActionLoading] = useState(false);
  const constanciaInputRef = useRef(null);

  // Trae la constancia YA guardada en Dropbox y la muestra embebida (igual que
  // loadSavedImagePreview hace con logo/foto de inmueble, pero vía el endpoint genérico de
  // Dropbox -- no existe un endpoint dedicado como el de logo/foto de inmueble).
  const loadConstanciaPreview = async (path) => {
    setConstanciaPreviewLoading(true);
    try {
      const response = await api.get('/dropbox/preview-file', {
        params: { path },
        responseType: 'blob',
      });
      setConstanciaPreview(URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' })));
    } catch (err) {
      setConstanciaPreview(null);
      setConstanciaError('El sistema indica que hay una constancia guardada, pero no se pudo descargar desde Dropbox. Probablemente la subida no se completó correctamente.');
    } finally {
      setConstanciaPreviewLoading(false);
    }
  };

  const handleConstanciaSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setConstanciaError('Solo se permiten archivos PDF');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setConstanciaError('El archivo no puede superar 10 MB');
      return;
    }
    setConstanciaFile(file);
    setConstanciaPreview(URL.createObjectURL(file));
    setConstanciaError('');
  };

  const handleConstanciaUpload = async () => {
    if (!constanciaFile || !formData.id) return false;
    setConstanciaUploading(true);
    setConstanciaError('');
    try {
      const fd = new FormData();
      fd.append('file', constanciaFile);
      const response = await api.post(`/companies/${formData.id}/constancia-fiscal`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setConstanciaSuccess(true);
      setConstanciaFile(null);
      setFormData(prev => ({ ...prev, constanciaFiscalPath: response.data.constanciaFiscalPath }));
      setTimeout(() => setConstanciaSuccess(false), 4000);
      return true;
    } catch (err) {
      setConstanciaError(err.response?.data?.error || 'Error al subir la constancia fiscal. Intenta de nuevo.');
      return false;
    } finally {
      setConstanciaUploading(false);
    }
  };

  const handleConstanciaDownload = async () => {
    if (!formData.constanciaFiscalPath) return;
    setConstanciaActionLoading(true);
    setConstanciaError('');
    try {
      const response = await api.get('/dropbox/download-file', {
        params: { path: formData.constanciaFiscalPath },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'constancia_fiscal.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setConstanciaError('No se pudo descargar la constancia fiscal.');
    } finally {
      setConstanciaActionLoading(false);
    }
  };

  const [formData, setFormData] = useState({
    businessName: '',
    legalName: '',
    rfc: '',
    constanciaFiscalPath: '',
    // Physical address fields
    street: '',
    exteriorNumber: '',
    interiorNumber: '',
    neighborhood: '',
    municipality: '',
    locality: '',
    state: 'Veracruz',
    postalCode: '',
    reference: '',
    betweenStreets: '',
    geolocation: '',
    // Fiscal address fields
    fiscalStreet: '',
    fiscalExteriorNumber: '',
    fiscalInteriorNumber: '',
    fiscalNeighborhood: '',
    fiscalMunicipality: '',
    fiscalState: 'Veracruz',
    fiscalPostalCode: '',
    fiscalReference: '',
    fiscalBetweenStreets: '',
    fiscalCountry: 'México',
    // Other fields
    businessSector: '',
    corporateContactTitle: '',
    corporateContactFirstName: '',
    corporateContactPaternalSurname: '',
    corporateContactMaternalSurname: '',
    corporateContactEmail: '',
    corporateContactPhone: '',
    legalRepresentative: '',
    insuranceCompany: '',
    insurancePolicyNumber: '',
    insurancePolicyValidity: '',
    insuranceSameForBranches: false,
    companyType: 'INDEPENDIENTE',
    isActive: true,

    // Campos del perfil de riesgo - Información del inmueble
    propertyType: '',
    propertyAddress: '',
    constructionArea: '',
    landArea: '',
    constructionLevels: '',
    employeesCount: '',
    floatingPopulation: '',
    maximumCapacity: '',
    mainActivities: '',
    administrativeHours: '',
    administrativeHoursStart: '',
    administrativeHoursEnd: '',
    propertyRegime: '',

    // Información de riesgo
    riskLevel: '',
    fireProtectionSystems: [],
    emergencyExits: '',
    emergencyPlan: false,
    
    // Información operacional
    operatingHoursStart: '',
    operatingHoursEnd: '',
    workDays: {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false
    },

    // Información de seguridad
    securityMeasures: [],
    previousIncidents: '',
    insuranceCoverage: '',
    
    // Observaciones
    additionalObservations: ''
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Obtener información del usuario
        const userResponse = await api.get('/users/me');
        setUserInfo(userResponse.data);
        console.log('Información de usuario cargada:', userResponse.data);
      } catch (err) {
        console.error('Error al cargar la información del usuario:', err);
        setError('No se pudo cargar la información del usuario.');
        setLoadErrorKind(err.response ? 'server' : 'network');
      }
      
      // Cargar sectores empresariales
      await fetchBusinessSectors();
      
      // Cargar empresas
      await fetchCompanies();
    };

    fetchInitialData();
  }, [reloadKey]);

  // Si llegamos desde otra pantalla (ej. Órdenes de Trabajo, al hacer click en un ente MATRIZ)
  // con una empresa específica a resaltar, abrir directamente su diálogo de edición en cuanto
  // los datos estén cargados, en vez de dejar al usuario buscarla en la lista.
  useEffect(() => {
    if (!location.state?.highlightEntityId) return;
    if (location.state.entityType && location.state.entityType !== 'MATRIZ') return;
    if (companies.length === 0) return;

    const company = companies.find(c => String(c.id) === String(location.state.highlightEntityId));
    if (company) {
      handleEditClick(company);
      if (location.state.highlightFieldKey) {
        setPendingHighlightFieldKey(location.state.highlightFieldKey);
      }
    }
    // Limpiar el state para no reabrir el diálogo en re-renders o al volver con "atrás"
    navigate(location.pathname + location.search, { replace: true, state: {} });
  }, [companies, location.state]);

  // A qué Accordion pertenece cada campo saltable desde "Validación de Campos" -- necesario
  // para abrirlo antes de intentar el scroll: los campos siguen montados en el DOM cuando su
  // Accordion está colapsado (MUI Collapse no los desmonta), pero con altura 0 no son visibles
  // ni tiene sentido hacer scrollIntoView sobre ellos.
  const FIELD_KEY_TO_SECTION = {
    NOMBRE_EMPRESA: 'general', RAZON_SOCIAL: 'general', RFC: 'general', GIRO_EMPRESA: 'general',
    CONTACTO_CORPORATIVO: 'contacto', EMAIL_CORPORATIVO: 'contacto', TELEFONO_CORPORATIVO: 'contacto',
    COMPANIA_SEGUROS: 'contacto', NUMERO_POLIZA: 'contacto', VIGENCIA_POLIZA: 'contacto',
    MUNICIPIO: 'direccionFisica', LOCALIDAD: 'direccionFisica', ESTADO: 'direccionFisica', GEOREFERENCIA: 'direccionFisica',
    NUMERO_EMPLEADOS: 'datosInmueble', AREA_CONSTRUCCION: 'datosInmueble', AREA_TERRENO: 'datosInmueble',
    NIVELES_CONSTRUCCION: 'datosInmueble', POBLACION_FLOTANTE: 'datosInmueble', CAPACIDAD_MAXIMA: 'datosInmueble',
    HORARIO_OPERATIVO: 'datosInmueble', HORARIO_ADMINISTRATIVO: 'datosInmueble', REGIMEN_PROPIEDAD: 'datosInmueble',
    ACTIVIDADES_PRINCIPALES: 'datosInmueble',
    LOGO_EMPRESA: 'logo', IMAGEN_INMUEBLE: 'fotoInmueble',
  };

  // Una vez abierto el diálogo de edición (arriba), si venimos con un campo específico a
  // resaltar (click en "Validación de Campos" desde Programas.jsx), abrimos primero el
  // Accordion que lo contiene y lo desplazamos a la vista/resaltamos brevemente para que el
  // usuario lo ubique sin buscar en todo el formulario.
  useEffect(() => {
    if (!dialogOpen || !pendingHighlightFieldKey) return;
    const key = pendingHighlightFieldKey;
    const section = FIELD_KEY_TO_SECTION[key];
    const needsExpand = section && !expandedSections[section];
    if (needsExpand) {
      setExpandedSections(prev => {
        const next = { ...prev, [section]: true };
        try { sessionStorage.setItem(SECTION_STORAGE_KEY, JSON.stringify(next)); } catch { /* no-op */ }
        return next;
      });
    }
    // Con Accordion por abrir se espera más (la animación de Collapse tarda ~300ms) antes de
    // medir posición y hacer scroll; si ya estaba abierto, el tiempo corto de siempre basta.
    const timer = setTimeout(() => {
      const el = document.getElementById(`field-${key}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const target = el.closest('.MuiFormControl-root') || el.closest('.MuiPaper-root') || el;
        target.style.transition = 'box-shadow 0.3s ease';
        target.style.boxShadow = '0 0 0 3px #ff9800';
        target.style.borderRadius = target.style.borderRadius || '8px';
        setTimeout(() => { target.style.boxShadow = ''; }, 2500);
      }
      setPendingHighlightFieldKey(null);
    }, needsExpand ? 550 : 400);
    return () => clearTimeout(timer);
  }, [dialogOpen, pendingHighlightFieldKey]);

  const fetchBusinessSectors = async () => {
    try {
      const response = await api.get('/business-sectors');
      const sectors = response.data;
      setBusinessSectors(sectors);
      
      // Establecer el primer sector como valor por defecto si existe y el campo está vacío
      if (sectors.length > 0) {
        setFormData(prev => {
          // Solo establecer si está vacío o undefined
          if (!prev.businessSector || prev.businessSector === '') {
            return { ...prev, businessSector: sectors[0].id };
          }
          return prev;
        });
      }
      
      console.log('Sectores empresariales cargados:', sectors.length);
    } catch (err) {
      console.error('Error al cargar sectores empresariales:', err);
    }
  };
  
  const handleSectorAdded = async (newSector) => {
    await fetchBusinessSectors();
    setFormData(prev => ({ ...prev, businessSector: newSector.id }));
    setSnackbar({ 
      open: true, 
      message: `Sector "${newSector.name}" creado exitosamente`, 
      severity: 'success' 
    });
  };

  // Limpiar timer al desmontar componente
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/companies');
      const companiesData = response.data;
      setCompanies(companiesData);
      setFilteredCompanies(companiesData);
      calculateStats(companiesData);
    } catch (err) {
      setError('Error al cargar las empresas');
      setLoadErrorKind(err.response ? 'server' : 'network');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const stats = {
      total: data.length,
      active: data.filter(c => c.isActive).length,
      inactive: data.filter(c => !c.isActive).length,
      matriz: data.filter(c => c.companyType === 'MATRIZ').length
    };
    setStats(stats);
  };

  // Filtrar empresas
  useEffect(() => {
    let filtered = companies;

    if (filters.businessName) {
      filtered = filtered.filter(c => 
        c.businessName?.toLowerCase().includes(filters.businessName.toLowerCase())
      );
    }

    if (filters.rfc) {
      filtered = filtered.filter(c => 
        c.rfc?.toLowerCase().includes(filters.rfc.toLowerCase())
      );
    }

    if (filters.businessSector) {
      filtered = filtered.filter(c => 
        c.businessSector?.toLowerCase().includes(filters.businessSector.toLowerCase())
      );
    }

    if (filters.companyType) {
      filtered = filtered.filter(c => c.companyType === filters.companyType);
    }

    if (filters.isActive !== '') {
      filtered = filtered.filter(c => c.isActive === (filters.isActive === 'true'));
    }

    setFilteredCompanies(filtered);
  }, [filters.businessName, filters.rfc, filters.businessSector, filters.companyType, filters.isActive, companies]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Funciones para manejar los campos del perfil de riesgo
  const handleDayChange = (day) => {
    setFormData(prev => ({
      ...prev,
      workDays: {
        ...prev.workDays,
        [day]: !prev.workDays[day]
      }
    }));
  };

  const handleArrayChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Validación de caracteres especiales para nombres y textos
  const sanitizeTextInput = (value, allowedChars = '') => {
    // Permite letras (con acentos), números, espacios y caracteres específicos permitidos
    const basePattern = 'a-zA-ZáéíóúÁÉÍÓÚñÑüÜ0-9\\s';
    const pattern = new RegExp(`[^${basePattern}${allowedChars}]`, 'g');
    return value.replace(pattern, '');
  };

  // Validación para nombres de empresas (permite . , - & /)
  const sanitizeBusinessName = (value) => {
    return sanitizeTextInput(value, '.,\\-&/');
  };

  // Validación para direcciones (permite . , - # /)
  const sanitizeAddress = (value) => {
    return sanitizeTextInput(value, '.,\\-#/');
  };

  // Validación para nombres de personas (permite . -)
  const sanitizePersonName = (value) => {
    return sanitizeTextInput(value, '.\\-');
  };

  // Validación para emails (permite @ . - _)
  const sanitizeEmail = (value) => {
    return value.replace(/[^a-zA-Z0-9@._-]/g, '');
  };

  // Validación para RFC (solo letras mayúsculas y números)
  const sanitizeRFC = (value) => {
    return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  };

  // Validación para teléfonos (solo números)
  const sanitizePhone = (value) => {
    return value.replace(/[^0-9]/g, '');
  };

  // Validación para números (solo dígitos)
  const sanitizeNumeric = (value) => {
    return value.replace(/[^0-9]/g, '');
  };

  const handleNumericChange = (field, value) => {
    // Validación para campos numéricos específicos
    let processedValue = value;
    
    if (['employeesCount', 'constructionArea', 'landArea'].includes(field)) {
      // Limitar a 5 dígitos
      const numericValue = value.replace(/[^0-9]/g, '');
      if (numericValue.length <= 5) {
        processedValue = numericValue;
      } else {
        return; // No actualizar si excede el límite
      }
    } else if (['constructionLevels', 'emergencyExits'].includes(field)) {
      // Limitar a 2 dígitos
      const numericValue = value.replace(/[^0-9]/g, '');
      if (numericValue.length <= 2) {
        processedValue = numericValue;
      } else {
        return; // No actualizar si excede el límite
      }
    } else if (['postalCode', 'fiscalPostalCode'].includes(field)) {
      // Código Postal: Permitir solo números (la validación de formato se maneja en el submit)
      processedValue = value.replace(/[^0-9]/g, '');
    } else if (['exteriorNumber', 'fiscalExteriorNumber'].includes(field)) {
      // Número Exterior: Limitar a 10 dígitos
      const numericValue = value.replace(/[^0-9]/g, '');
      if (numericValue.length <= 10) {
        processedValue = numericValue;
      } else {
        return; // No actualizar si excede el límite
      }
    } else if (field === 'corporateContactPhone') {
      // Teléfono: Limitar a 10 dígitos
      const numericValue = value.replace(/[^0-9]/g, '');
      if (numericValue.length <= 10) {
        processedValue = numericValue;
        // Validar en tiempo real
        const validation = validatePhoneFormat(numericValue);
        setPhoneValidation(validation);
      } else {
        return; // No actualizar si excede el límite
      }
    }

    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));
  };

  // Función para generar datos de prueba aleatorios
  const generateTestData = () => {
    const companies = [
      {
        businessName: 'Tecnologías Innovadoras S.A. de C.V.',
        legalName: 'Tecnologías Innovadoras Sociedad Anónima de Capital Variable',
        sector: 'Tecnología y Software',
        title: 'Lic.',
        firstName: 'María Elena',
        paternalSurname: 'Rodríguez',
        maternalSurname: 'González',
        email: 'contacto@tecnologiasinnovadoras.com.mx',
        representative: 'Ing. Carlos Alberto Mendoza López'
      },
      {
        businessName: 'Constructora del Valle S.A. de C.V.',
        legalName: 'Constructora del Valle Sociedad Anónima de Capital Variable',
        sector: 'Construcción e Inmobiliario',
        title: 'Arq.',
        firstName: 'José Luis',
        paternalSurname: 'Hernández',
        maternalSurname: 'Pérez',
        email: 'info@constructoradelvalle.com.mx',
        representative: 'Ing. Civil Ana Patricia Morales'
      },
      {
        businessName: 'Comercializadora Global S.A. de C.V.',
        legalName: 'Comercializadora Global Sociedad Anónima de Capital Variable',
        sector: 'Comercio y Retail',
        title: 'Lic.',
        firstName: 'Roberto Carlos',
        paternalSurname: 'Jiménez',
        maternalSurname: 'López',
        email: 'ventas@comercializadoraglobal.com.mx',
        representative: 'C.P. Laura Beatriz Sánchez'
      },
      {
        businessName: 'Servicios Financieros Modernos S.A. de C.V.',
        legalName: 'Servicios Financieros Modernos Sociedad Anónima de Capital Variable',
        sector: 'Servicios Financieros',
        title: 'Lic.',
        firstName: 'Fernando Alejandro',
        paternalSurname: 'Torres',
        maternalSurname: 'Castro',
        email: 'contacto@serviciosfinancieros.com.mx',
        representative: 'Lic. Econ. Patricia Guadalupe Ramírez'
      },
      {
        businessName: 'Industrias Manufactureras del Norte S.A. de C.V.',
        legalName: 'Industrias Manufactureras del Norte Sociedad Anónima de Capital Variable',
        sector: 'Manufactura e Industria',
        title: 'Ing.',
        firstName: 'Miguel Ángel',
        paternalSurname: 'Vázquez',
        maternalSurname: 'Delgado',
        email: 'produccion@industriasnorte.com.mx',
        representative: 'Ing. Mec. Sandra Leticia Flores'
      }
    ];
    
    const randomCompany = companies[Math.floor(Math.random() * companies.length)];
    const randomNumber = Math.floor(Math.random() * 900000 + 100000);
    const randomRFC = randomCompany.businessName.substring(0, 3).toUpperCase() + randomNumber + 'ABC';
    
    return {
      ...randomCompany,
      rfc: randomRFC,
      phone: '+52 55 ' + Math.floor(Math.random() * 9000 + 1000) + '-' + Math.floor(Math.random() * 9000 + 1000)
    };
  };

  const hasFormData = () => {
    const fieldsToCheck = [
      'businessName', 'legalName', 'rfc',
      'street', 'exteriorNumber', 'neighborhood', 'municipality', 'postalCode',
      'fiscalStreet', 'fiscalNeighborhood', 'fiscalMunicipality', 'fiscalPostalCode',
      'corporateContactTitle', 'corporateContactFirstName', 'corporateContactPaternalSurname',
      'corporateContactEmail', 'corporateContactPhone', 'legalRepresentative',
    ];
    return fieldsToCheck.some(f => formData[f] && String(formData[f]).trim() !== '');
  };

  const handleCloseAttempt = () => {
    if (!isEditMode && hasFormData()) {
      setDiscardConfirmOpen(true);
    } else {
      setDialogOpen(false);
      setValidationError('');
      if (isEditMode) setIsEditMode(false);
      clearForm();
    }
  };

  // Función para limpiar el formulario (campos vacíos)
  const clearForm = () => {
    setFormData({
      // Información General
      businessName: '',
      legalName: '',
      rfc: '',
      constanciaFiscalPath: '',
      businessSector: businessSectors.length > 0 ? businessSectors[0].id : '',

      // Dirección Física
      street: '',
      exteriorNumber: '',
      interiorNumber: '',
      neighborhood: '',
      municipality: '',
      locality: '',
      state: 'Veracruz',
      postalCode: '',
      reference: '',
      betweenStreets: '',
      geolocation: '',
      
      // Dirección Fiscal
      fiscalStreet: '',
      fiscalExteriorNumber: '',
      fiscalInteriorNumber: '',
      fiscalNeighborhood: '',
      fiscalMunicipality: '',
      fiscalState: 'Veracruz',
      fiscalPostalCode: '',
      fiscalReference: '',
      fiscalBetweenStreets: '',
      fiscalCountry: 'México',
      
      // Información de Contacto
      corporateContactTitle: '',
      corporateContactFirstName: '',
      corporateContactPaternalSurname: '',
      corporateContactMaternalSurname: '',
      corporateContactEmail: '',
      corporateContactPhone: '',
      legalRepresentative: '',
      insuranceCompany: '',
      insurancePolicyNumber: '',
      insurancePolicyValidity: '',
      insuranceSameForBranches: false,

      // Configuración
      companyType: 'INDEPENDIENTE',
      isActive: true,

      // Campos del perfil de riesgo - Información del inmueble
      propertyType: '',
      propertyAddress: '',
      constructionArea: '',
      landArea: '',
      constructionLevels: '',
      employeesCount: '',
      floatingPopulation: '',
      maximumCapacity: '',
      mainActivities: '',
      administrativeHours: '',
      administrativeHoursStart: '',
      administrativeHoursEnd: '',
      propertyRegime: '',

      // Información de riesgo
      riskLevel: '',
      fireProtectionSystems: [],
      emergencyExits: '',
      emergencyPlan: false,
      
      // Información operacional
      operatingHoursStart: '',
      operatingHoursEnd: '',
      workDays: {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false,
        sunday: false
      },

      // Información de seguridad
      securityMeasures: [],
      previousIncidents: '',
      insuranceCoverage: '',
      
      // Observaciones
      additionalObservations: ''
    });
    setValidationError('');
    setRfcValidation({ isValid: true, message: '', tipo: '' });
    setEmailValidation({ isValid: true, message: '' });
    setPostalCodeValidation({ isValid: true, message: '' });
    setFiscalPostalCodeValidation({ isValid: true, message: '' });
    setGeolocationValidation({ isValid: true, message: '' });
    setPhoneValidation({ isValid: true, message: '' });
    setLegalRepresentativeValidation({ isValid: true, message: '' });
  };

  // Función para llenar el formulario con datos de prueba
  const resetForm = () => {
    const testData = generateTestData();
    
    setFormData({
      // Información General
      businessName: testData.businessName,
      legalName: testData.legalName,
      rfc: testData.rfc,
      businessSector: testData.sector,
      
      // Dirección Física
      street: 'Av. Revolución',
      exteriorNumber: '1234',
      interiorNumber: 'A',
      neighborhood: 'Centro',
      municipality: 'Cuauhtémoc',
      locality: 'Ciudad de México',
      state: 'Ciudad de México',
      postalCode: '06000',
      reference: 'Entre Calle Morelos y Calle Hidalgo',
      betweenStreets: 'Morelos y Hidalgo',
      geolocation: '19.4326, -99.1332',
      
      // Dirección Fiscal
      fiscalStreet: 'Av. Revolución',
      fiscalExteriorNumber: '1234',
      fiscalInteriorNumber: 'A',
      fiscalNeighborhood: 'Centro',
      fiscalMunicipality: 'Cuauhtémoc',
      fiscalState: 'Ciudad de México',
      fiscalPostalCode: '06000',
      fiscalReference: 'Entre Calle Morelos y Calle Hidalgo',
      fiscalBetweenStreets: 'Morelos y Hidalgo',
      fiscalCountry: 'México',
      
      // Información de Contacto
      corporateContactTitle: testData.title,
      corporateContactFirstName: testData.firstName,
      corporateContactPaternalSurname: testData.paternalSurname,
      corporateContactMaternalSurname: testData.maternalSurname,
      corporateContactEmail: testData.email,
      corporateContactPhone: testData.phone,
      legalRepresentative: testData.representative,
      
      // Configuración
      companyType: 'INDEPENDIENTE',
      isActive: true,
      
      // Campos del perfil de riesgo - Información del inmueble
      propertyType: propertyTypes[Math.floor(Math.random() * propertyTypes.length)],
      propertyAddress: 'Av. Insurgentes Sur 1234, Col. Del Valle, CDMX',
      constructionArea: String(Math.floor(Math.random() * 5000) + 500),
      landArea: String(Math.floor(Math.random() * 8000) + 1000),
      constructionLevels: String(Math.floor(Math.random() * 10) + 1),
      employeesCount: String(Math.floor(Math.random() * 200) + 10),
      maximumCapacity: String(Math.floor(Math.random() * 200) + 10),
      administrativeHours: '09:00 - 18:00',
      administrativeHoursStart: '09:00',
      administrativeHoursEnd: '18:00',
      propertyRegime: 'PROPIO',

      // Información de riesgo
      riskLevel: riskLevels[Math.floor(Math.random() * riskLevels.length)].value,
      fireProtectionSystems: [fireProtectionOptions[0], fireProtectionOptions[2]],
      emergencyExits: String(Math.floor(Math.random() * 8) + 2),
      emergencyPlan: Math.random() > 0.5,
      
      // Información operacional
      operatingHoursStart: '08:00',
      operatingHoursEnd: '18:00',
      workDays: {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false,
        sunday: false
      },
      mainActivities: 'Actividades principales de la empresa según su giro comercial',
      
      // Información de seguridad
      securityMeasures: [securityMeasuresOptions[0], securityMeasuresOptions[1]],
      previousIncidents: '',
      insuranceCoverage: 'Póliza de seguro contra incendios y responsabilidad civil',
      
      // Observaciones
      additionalObservations: 'Observaciones adicionales sobre el perfil de riesgo de la empresa'
    });
    setValidationError('');
    setGeolocationValidation({ isValid: true, message: '' });
    setPhoneValidation({ isValid: true, message: '' });
    setLegalRepresentativeValidation({ isValid: true, message: '' });
  };

  // Función para manejar cambio de email con validación en tiempo real
  const handleEmailChange = (newEmail) => {
    setFormData(prev => ({ ...prev, corporateContactEmail: newEmail }));
    
    // Validar formato en tiempo real
    const validation = validateEmailFormat(newEmail);
    setEmailValidation(validation);
    
    // Limpiar error general si el email es válido
    if (validation.isValid && validationError.includes('email') || validationError.includes('correo')) {
      setValidationError('');
    }
  };

  // Función para manejar cambio de código postal con validación en tiempo real
  const handlePostalCodeChange = (value) => {
    // Permitir solo números
    const numericValue = value.replace(/[^0-9]/g, '');
    setFormData(prev => ({ ...prev, postalCode: numericValue }));
    
    // Validar formato en tiempo real
    const validation = validatePostalCode(numericValue);
    setPostalCodeValidation(validation);
  };

  // Función para manejar cambio de código postal fiscal con validación en tiempo real
  const handleFiscalPostalCodeChange = (value) => {
    // Permitir solo números
    const numericValue = value.replace(/[^0-9]/g, '');
    setFormData(prev => ({ ...prev, fiscalPostalCode: numericValue }));
    
    // Validar formato en tiempo real
    const validation = validatePostalCode(numericValue);
    setFiscalPostalCodeValidation(validation);
  };

  // Función para manejar cambio de RFC con validación en tiempo real
  const handleRFCChange = (e) => {
    const newRFC = sanitizeRFC(e.target.value);
    setFormData(prev => ({ ...prev, rfc: newRFC }));
    
    // Validar formato en tiempo real
    const validation = validateRFCFormat(newRFC);
    setRfcValidation(validation);
    
    // Limpiar error general si el RFC es válido
    if (validation.isValid && validationError.includes('RFC')) {
      setValidationError('');
    }
  };

  // Función para manejar cambio de geolocalización con validación en tiempo real
  const handleGeolocationChange = (newGeolocation) => {
    setFormData(prev => ({ ...prev, geolocation: newGeolocation }));
    
    // Validar en tiempo real
    const validation = validateGeolocation(newGeolocation);
    setGeolocationValidation(validation);
    
    // Limpiar error general si la geolocalización es válida
    if (validation.isValid && validationError.includes('geolocalización')) {
      setValidationError('');
    }
  };

  // Función para validar formato de email
  const validateEmailFormat = (email) => {
    if (!email || email.trim() === '') {
      return { isValid: true, message: '' }; // Email es opcional
    }
    
    // Regex para validar formato de email (requiere dominio con extensión .com, .org, etc)
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    if (!emailRegex.test(email.trim())) {
      return { 
        isValid: false, 
        message: 'Formato de email inválido. Debe incluir @dominio.com' 
      };
    }
    
    return { isValid: true, message: '✓ Formato de email válido' };
  };

  // Función para validar formato de representante legal
  const validateLegalRepresentativeFormat = (value) => {
    if (!value || value.trim() === '') {
      return { isValid: true, message: '' };
    }
    
    // Regex para permitir solo letras, espacios, punto y guion
    const legalRepresentativeRegex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s.-]+$/;
    
    if (!legalRepresentativeRegex.test(value.trim())) {
      return { isValid: false, message: 'El campo solo permite letras, espacios, punto (.) y guion (-).' };
    }
    
    return { isValid: true, message: '✓ Formato válido' };
  };

  // Función para validar formato de teléfono corporativo
  const validatePhoneFormat = (phone) => {
    if (!phone || phone.trim() === '') {
      return { isValid: false, message: 'El número telefónico debe contener exactamente 10 dígitos.' };
    }
    
    // Limpiar caracteres no numéricos
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    
    // Validar que tenga exactamente 10 dígitos
    if (cleanPhone.length !== 10) {
      return { isValid: false, message: 'El número telefónico debe contener exactamente 10 dígitos.' };
    }
    
    return { isValid: true, message: '✓ Número telefónico válido' };
  };

  // Función para validar formato de RFC
  const validateRFCFormat = (rfc) => {
    if (!rfc || rfc.trim() === '') {
      return { isValid: true, message: '' };
    }

    // Limpiar espacios
    const cleanRFC = rfc.trim().toUpperCase();

    // Validar longitud (12 o 13 caracteres)
    if (cleanRFC.length < 12 || cleanRFC.length > 13) {
      return { isValid: false, message: 'El RFC debe tener 12 o 13 caracteres' };
    }

    // Patrones para RFC de personas físicas (13 caracteres) y morales (12 caracteres)
    const rfcPersonaFisica = /^[A-Z&Ñ]{4}[0-9]{6}[A-Z0-9]{3}$/; // AAAA######XXX
    const rfcPersonaMoral = /^[A-Z&Ñ]{3}[0-9]{6}[A-Z0-9]{3}$/;   // AAA######XXX

    let isValidFormat = false;
    let tipoRFC = '';

    if (cleanRFC.length === 13 && rfcPersonaFisica.test(cleanRFC)) {
      isValidFormat = true;
      tipoRFC = 'Persona Física';
    } else if (cleanRFC.length === 12 && rfcPersonaMoral.test(cleanRFC)) {
      isValidFormat = true;
      tipoRFC = 'Persona Moral';
    }

    if (!isValidFormat) {
      return { 
        isValid: false, 
        message: 'Formato de RFC inválido. Debe ser: AAA######XXX (12 chars) o AAAA######XXX (13 chars)' 
      };
    }

    // Validar que no contenga palabras prohibidas
    const palabrasProhibidas = ['BUEI', 'BUEY', 'CACA', 'CACO', 'CAGA', 'CAGO', 'CAKA', 'CAKO', 'COGE', 'COGI', 'COJA', 'COJE', 'COJI', 'COJO', 'COLA', 'CULO', 'FALO', 'FETO', 'GETA', 'GUEI', 'GUEY', 'JETA', 'JOTO', 'KACA', 'KACO', 'KAGA', 'KAGO', 'KAKA', 'KAKO', 'KOGE', 'KOGI', 'KOJA', 'KOJE', 'KOJI', 'KOJO', 'KOLA', 'KULO', 'LILO', 'LOCA', 'LOCO', 'LOKA', 'LOKO', 'MAME', 'MAMO', 'MEAR', 'MEAS', 'MEON', 'MIAR', 'MION', 'MOCO', 'MOKO', 'MULA', 'MULO', 'NACA', 'NACO', 'PEDA', 'PEDO', 'PENE', 'PIPI', 'PITO', 'POPO', 'PUTA', 'PUTO', 'QULO', 'RATA', 'ROBA', 'ROBE', 'ROBO', 'RUIN', 'SENO', 'TETA', 'VACA', 'VAGA', 'VAGO', 'VAKA', 'VUEI', 'VUEY', 'WUEI', 'WUEY'];
    
    const iniciales = cleanRFC.substring(0, cleanRFC.length === 13 ? 4 : 3);
    if (palabrasProhibidas.includes(iniciales)) {
      return { 
        isValid: false, 
        message: 'El RFC contiene una combinación de letras no permitida por el SAT' 
      };
    }

    // Validar sección de fecha AAMMDD
    const fechaInicio = cleanRFC.length === 13 ? 4 : 3;
    const fechaStr = cleanRFC.substring(fechaInicio, fechaInicio + 6);
    
    if (!validateRFCDate(fechaStr)) {
      return {
        isValid: false,
        message: 'El RFC es incorrecto: la sección de fecha (AAMMDD) no corresponde a una fecha válida.'
      };
    }

    return { 
      isValid: true, 
      message: `RFC válido (${tipoRFC})`,
      tipo: tipoRFC 
    };
  };

  // Función para validar fecha AAMMDD del RFC
  const validateRFCDate = (fechaStr) => {
    if (!fechaStr || fechaStr.length !== 6) {
      return false;
    }

    // Extraer año, mes y día
    const aa = parseInt(fechaStr.substring(0, 2), 10);
    const mm = parseInt(fechaStr.substring(2, 4), 10);
    const dd = parseInt(fechaStr.substring(4, 6), 10);

    // Validar rangos básicos
    if (mm < 1 || mm > 12 || dd < 1 || dd > 31) {
      return false;
    }

    // Determinar el año completo (considerando el rango de RFCs)
    // Para RFCs, generalmente se usa 1900 + aa para aa >= 50, y 2000 + aa para aa < 50
    const year = aa >= 50 ? 1900 + aa : 2000 + aa;

    // Validar que la fecha exista usando el objeto Date
    const date = new Date(year, mm - 1, dd);
    
    // Verificar que la fecha creada sea válida
    // El objeto Date ajusta automáticamente las fechas inválidas, por lo que comparamos
    return (
      date.getFullYear() === year &&
      date.getMonth() === mm - 1 &&
      date.getDate() === dd
    );
  };

  // Función para validar RFC al intentar guardar
  const handleEditClick = (company) => {
    setEntityLock(null);
    api.get(`/companies/${company.id}/lock-status`)
      .then(res => setEntityLock(res.data))
      .catch(() => setEntityLock(null));
    setIsEditMode(true);
    setCompanyHasPropertyImage(!!company.hasPropertyImage);
    setPropertyImageFile(null);
    setPropertyImagePreview(null);
    setPropertyImageError('');
    setCompanyHasLogo(!!company.hasLogo);
    setLogoFile(null);
    setLogoPreview(null);
    setLogoError('');
    setConstanciaFile(null);
    setConstanciaPreview(null);
    setConstanciaError('');
    if (company.id) {
      if (company.hasPropertyImage) loadSavedImagePreview(company.id, 'property-image');
      if (company.hasLogo) loadSavedImagePreview(company.id, 'logo');
      if (company.constanciaFiscalPath) loadConstanciaPreview(company.constanciaFiscalPath);
    }
    setFormData({
      id: company.id || '',
      constanciaFiscalPath: company.constanciaFiscalPath || '',
      businessName: company.businessName || '',
      legalName: company.legalName || '',
      rfc: company.rfc || '',
      street: company.street || '',
      exteriorNumber: company.exteriorNumber || '',
      interiorNumber: company.interiorNumber || '',
      neighborhood: company.neighborhood || '',
      municipality: company.municipality || '',
      locality: company.locality || '',
      state: company.state || '',
      postalCode: company.postalCode || '',
      reference: company.reference || '',
      betweenStreets: company.betweenStreets || '',
      geolocation: company.geolocation || '',
      fiscalStreet: company.fiscalStreet || '',
      fiscalExteriorNumber: company.fiscalExteriorNumber || '',
      fiscalInteriorNumber: company.fiscalInteriorNumber || '',
      fiscalNeighborhood: company.fiscalNeighborhood || '',
      fiscalMunicipality: company.fiscalMunicipality || '',
      fiscalState: company.fiscalState || '',
      fiscalPostalCode: company.fiscalPostalCode || '',
      fiscalReference: company.fiscalReference || '',
      fiscalBetweenStreets: company.fiscalBetweenStreets || '',
      fiscalCountry: company.fiscalCountry || 'México',
      businessSector: company.businessSectorId || company.businessSector || '',
      corporateContactTitle: company.corporateContactTitle || '',
      corporateContactFirstName: company.corporateContactFirstName || '',
      corporateContactPaternalSurname: company.corporateContactPaternalSurname || '',
      corporateContactMaternalSurname: company.corporateContactMaternalSurname || '',
      corporateContactEmail: company.corporateContactEmail || '',
      corporateContactPhone: company.corporateContactPhone || '',
      legalRepresentative: company.legalRepresentative || '',
      insuranceCompany: company.insuranceCompany || '',
      insurancePolicyNumber: company.insurancePolicyNumber || '',
      insurancePolicyValidity: company.insurancePolicyValidity || '',
      insuranceSameForBranches: company.insuranceSameForBranches || false,
      companyType: company.companyType || 'INDEPENDIENTE',
      isActive: company.isActive !== undefined ? company.isActive : true,
      
      propertyType: company.propertyType || '',
      propertyAddress: company.propertyAddress || '',
      constructionArea: company.constructionArea || '',
      landArea: company.landArea || '',
      constructionLevels: company.constructionLevels || '',
      employeesCount: company.employeesCount || '',
      floatingPopulation: company.floatingPopulation || '',
      maximumCapacity: company.maximumCapacity || '',
      administrativeHours: company.administrativeHours || '',
      administrativeHoursStart: company.administrativeHoursStart || '',
      administrativeHoursEnd: company.administrativeHoursEnd || '',
      propertyRegime: company.propertyRegime || '',
      riskLevel: company.riskLevel || '',
      fireProtectionSystems: company.fireProtectionSystems || [],
      emergencyExits: company.emergencyExits || '',
      emergencyPlan: company.emergencyPlan || false,
      operatingHoursStart: company.operatingHoursStart || '',
      operatingHoursEnd: company.operatingHoursEnd || '',
      workDays: company.workDays || {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false,
        sunday: false
      },
      mainActivities: company.mainActivities || '',
      securityMeasures: company.securityMeasures || [],
      previousIncidents: company.previousIncidents || '',
      insuranceCoverage: company.insuranceCoverage || '',
      additionalObservations: company.additionalObservations || ''
    });
    setDialogOpen(true);
    setValidationError('');
    setGeolocationValidation({ isValid: true, message: '' });
  };

  // Archivos elegidos pero sin subir (solo existen al editar: las secciones de logo, fotografía y
  // constancia no se muestran al crear). Al guardar se pregunta si se suben para no perderlos.
  const [pendingUploadConfirm, setPendingUploadConfirm] = useState(false);
  const pendingUploads = isEditMode && formData.id ? [
    { label: 'Logo', file: logoFile, upload: handleLogoUpload },
    { label: 'Fotografía del inmueble', file: propertyImageFile, upload: handlePropertyImageUpload },
    { label: 'Constancia Fiscal', file: constanciaFile, upload: handleConstanciaUpload },
  ].filter(u => u.file) : [];

  const handleSaveClick = () => {
    if (pendingUploads.length > 0) {
      setPendingUploadConfirm(true);
      return;
    }
    handleSubmit();
  };

  const handleSubmit = async ({ uploadPending = false } = {}) => {
    try {
      setLoading(true);
      setValidationError('');
      setFieldErrors({}); // Limpiar errores previos
      
      if (isEditMode && !formData.id) {
        setValidationError('No se ha seleccionado ninguna empresa para editar');
        setLoading(false);
        return;
      }

      // Objeto para acumular errores de campos
      const errors = {};

      // Validar TODOS los campos obligatorios y acumular errores
      if (!formData.businessName) errors.businessName = true;
      
      // Validar dirección física
      if (!formData.street) errors.street = true;
      if (!formData.exteriorNumber) errors.exteriorNumber = true;
      if (!formData.neighborhood) errors.neighborhood = true;
      if (!formData.municipality) errors.municipality = true;
      if (!formData.locality) errors.locality = true;
      if (!formData.state) errors.state = true;

      // Validar código postal fiscal solo si tiene contenido (no es requerido)
      if (formData.fiscalPostalCode && formData.fiscalPostalCode.trim() !== '') {
        const fiscalPostalValidation = validatePostalCode(formData.fiscalPostalCode);
        setFiscalPostalCodeValidation(fiscalPostalValidation);
        if (!fiscalPostalValidation.isValid) {
          errors.fiscalPostalCode = true;
        }
      } else {
        // Si está vacío, es válido (no es requerido)
        setFiscalPostalCodeValidation({ isValid: true, message: '' });
      }

      // Validar código postal físico
      const postalValidation = validatePostalCode(formData.postalCode);
      setPostalCodeValidation(postalValidation);
      if (!postalValidation.isValid) {
        errors.postalCode = true;
      }

      // Validar información de contacto
      if (!formData.corporateContactTitle) errors.corporateContactTitle = true;
      if (!formData.corporateContactFirstName) errors.corporateContactFirstName = true;
      if (!formData.corporateContactPaternalSurname) errors.corporateContactPaternalSurname = true;
      if (!formData.corporateContactEmail) errors.corporateContactEmail = true;
      
      // Validar teléfono corporativo (exactamente 10 dígitos)
      const phoneValidation = validatePhoneFormat(formData.corporateContactPhone);
      setPhoneValidation(phoneValidation);
      if (!phoneValidation.isValid) {
        errors.corporateContactPhone = true;
      }
      
      // Representante legal es opcional -- solo se valida el formato si se capturó algo.
      if (formData.legalRepresentative && formData.legalRepresentative.trim()) {
        const legalRepValidation = validateLegalRepresentativeFormat(formData.legalRepresentative);
        setLegalRepresentativeValidation(legalRepValidation);
        if (!legalRepValidation.isValid) {
          errors.legalRepresentative = true;
        }
      } else {
        setLegalRepresentativeValidation({ isValid: true, message: '' });
      }

      // Si hay errores, mostrarlos TODOS y detener
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        const camposFaltantes = Object.keys(errors).length;
        const missingFieldsMessage = `Por favor, completa todos los campos obligatorios. Faltan ${camposFaltantes} campo${camposFaltantes > 1 ? 's' : ''} por llenar.`;
        setValidationError(missingFieldsMessage);
        // Antes este mensaje solo quedaba en el estado (usado nada más para decorar RFC/email) --
        // nunca se mostraba al usuario. El resaltado naranja de los campos es fácil de pasar por
        // alto si el campo faltante quedó fuera de la vista al hacer scroll; este Snackbar sí se
        // ve siempre, sin importar en qué parte del formulario esté el usuario.
        setSnackbar({ open: true, message: `⚠️ ${missingFieldsMessage}`, severity: 'error' });
        setLoading(false);
        return;
      }

      // Validar RFC solo si se proporcionó
      const rfcValue = formData.rfc ? formData.rfc.trim() : '';
      if (rfcValue && rfcValue.length > 0) {
        // Validar formato tanto en modo crear como en modo edición
        const rfcFormatValidation = validateRFCFormat(rfcValue);
        if (!rfcFormatValidation.isValid) {
          setValidationError(rfcFormatValidation.message);
          setLoading(false);
          
          const rfcField = document.querySelector('input[value="' + rfcValue + '"]');
          if (rfcField) {
            rfcField.focus();
            rfcField.select();
          }
          return;
        }
      }

      // Validar formato de email si se proporcionó
      if (formData.corporateContactEmail && formData.corporateContactEmail.trim() !== '') {
        const emailFormatValidation = validateEmailFormat(formData.corporateContactEmail);
        if (!emailFormatValidation.isValid) {
          setValidationError(emailFormatValidation.message);
          setEmailValidation(emailFormatValidation);
          setLoading(false);
          
          // Enfocar el campo email para que el usuario lo corrija
          const emailField = document.querySelector('input[name="corporateContactEmail"]');
          if (emailField) {
            emailField.focus();
            emailField.select();
          }
          return;
        }
      }

      // Filtrar solo los campos que pertenecen a la empresa (no del perfil de riesgo)
      const companyPayload = {
        businessName: formData.businessName,
        legalName: formData.legalName,
        rfc: formData.rfc,
        // Physical address fields
        street: formData.street,
        exteriorNumber: formData.exteriorNumber,
        interiorNumber: formData.interiorNumber,
        neighborhood: formData.neighborhood,
        municipality: formData.municipality,
        locality: formData.locality,
        state: formData.state,
        postalCode: formData.postalCode,
        reference: formData.reference,
        betweenStreets: formData.betweenStreets,
        geolocation: formData.geolocation,
        // Fiscal address fields
        fiscalStreet: formData.fiscalStreet,
        fiscalExteriorNumber: formData.fiscalExteriorNumber,
        fiscalInteriorNumber: formData.fiscalInteriorNumber,
        fiscalNeighborhood: formData.fiscalNeighborhood,
        fiscalMunicipality: formData.fiscalMunicipality,
        fiscalState: formData.fiscalState,
        fiscalPostalCode: formData.fiscalPostalCode,
        fiscalReference: formData.fiscalReference,
        fiscalBetweenStreets: formData.fiscalBetweenStreets,
        fiscalCountry: formData.fiscalCountry,
        // Other company fields
        businessSector: formData.businessSector,
        corporateContactTitle: formData.corporateContactTitle,
        corporateContactFirstName: formData.corporateContactFirstName,
        corporateContactPaternalSurname: formData.corporateContactPaternalSurname,
        corporateContactMaternalSurname: formData.corporateContactMaternalSurname,
        corporateContactEmail: formData.corporateContactEmail,
        corporateContactPhone: formData.corporateContactPhone,
        legalRepresentative: formData.legalRepresentative,
        insuranceCompany: formData.insuranceCompany,
        insurancePolicyNumber: formData.insurancePolicyNumber,
        insurancePolicyValidity: formData.insurancePolicyValidity,
        insuranceSameForBranches: formData.insuranceSameForBranches,
        companyType: formData.companyType,
        isActive: formData.isActive,
        // Datos del Inmueble (opcionales)
        employeesCount: formData.employeesCount ? parseInt(formData.employeesCount) : null,
        constructionArea: formData.constructionArea ? parseFloat(formData.constructionArea) : null,
        landArea: formData.landArea ? parseFloat(formData.landArea) : null,
        floatingPopulation: formData.floatingPopulation ? parseInt(formData.floatingPopulation) : null,
        constructionLevels: formData.constructionLevels ? parseInt(formData.constructionLevels) : null,
        maximumCapacity: formData.maximumCapacity ? parseInt(formData.maximumCapacity) : null,
        mainActivities: formData.mainActivities || null,
        administrativeHours: formData.administrativeHours || null,
        administrativeHoursStart: formData.administrativeHoursStart || null,
        administrativeHoursEnd: formData.administrativeHoursEnd || null,
        operatingHoursStart: formData.operatingHoursStart || null,
        operatingHoursEnd: formData.operatingHoursEnd || null,
        propertyRegime: formData.propertyRegime || null
      };

      // Llamar a la API según el modo
      let response;
      let updatedCompanies;
      
      if (isEditMode) {
        response = await api.put(`/companies/${formData.id}`, companyPayload);
        updatedCompanies = companies.map(c => c.id === formData.id ? response.data : c);
        setCompanies(updatedCompanies);
        setFilteredCompanies(updatedCompanies);
        calculateStats(updatedCompanies);
        setSnackbar({ open: true, message: `Prospecto "${response.data.businessName}" actualizado.`, severity: 'success' });
        if (uploadPending) {
          // Uno por uno: cada subida marca su propio error en su sección si falla
          const failed = [];
          for (const item of pendingUploads) {
            if (!(await item.upload())) failed.push(item.label);
          }
          if (failed.length > 0) {
            setSnackbar({ open: true, severity: 'error',
              message: `Los datos se guardaron, pero no se pudo subir: ${failed.join(', ')}. Revisa el error en su sección e intenta de nuevo.` });
            return;
          }
          setSnackbar({ open: true, message: `Prospecto "${response.data.businessName}" actualizado y archivos subidos.`, severity: 'success' });
        }
      } else {
        response = await api.post('/companies', companyPayload);
        const newCompany = response.data;
        
        setSnackbar({
          open: true,
          message: newCompany.corporateContactEmail
            ? `Prospecto creado. La invitación al portal se enviará a ${newCompany.corporateContactEmail} en unos momentos.`
            : 'Prospecto creado.',
          severity: 'success'
        });
        
        updatedCompanies = [...companies, newCompany];
        setCompanies(updatedCompanies);
        setFilteredCompanies(updatedCompanies);
        calculateStats(updatedCompanies);
      }
      
      setDialogOpen(false);
      setError(null);
      setIsEditMode(false);
      clearForm();
      if (isEditMode && returnToValidation) {
        goBackToValidation(navigate, returnToValidation);
      }
    } catch (err) {
      const rfcDuplicate = err.response?.status === 400 && err.response?.data?.message?.includes('RFC');
      if (rfcDuplicate) {
        setValidationError(`El RFC "${formData.rfc}" ya está registrado en el sistema. Por favor, verifica e ingresa un RFC diferente.`);
        const rfcField = document.querySelector('input[value="' + formData.rfc + '"]');
        if (rfcField) {
          rfcField.focus();
          rfcField.select();
        }
      } else if (err.response?.status === 400 && (err.response?.data?.message?.includes('email') || err.response?.data?.message?.includes('correo'))) {
        // Error específico de email - mostrar mensaje del backend
        const backendMessage = err.response?.data?.message || '';
        setValidationError(backendMessage || 'El correo electrónico del contacto corporativo es inválido. Por favor, verifica el formato (ejemplo: usuario@empresa.com).');
        setEmailValidation({ isValid: false, message: backendMessage });
        const emailField = document.querySelector('input[name="corporateContactEmail"]');
        if (emailField) {
          emailField.focus();
          emailField.select();
        }
      } else {
        // Error genérico - mostrar mensaje detallado del backend
        const errorMessage = err.response?.data?.message || err.message || 'Error desconocido';
        const action = isEditMode ? 'editar' : 'crear';
        setValidationError(`Error al ${action} la empresa: ${errorMessage}`);
        setError(`Error al ${action} la empresa. Revise los datos e intente nuevamente.`);
      }
      console.error('Error completo:', err);
      console.error('Respuesta del servidor:', err.response?.data);
      
      // Mostrar snackbar con el error
      const action = isEditMode ? 'editar' : 'crear';
      setSnackbar({
        open: true,
        message: rfcDuplicate
          ? `El RFC "${formData.rfc}" ya está registrado en otra empresa.`
          : `Error al ${action} la empresa. Revise los datos marcados en rojo y corrija antes de reintentar.`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDeleteConfirm = () => {
    if (loading) return;
    setDeleteConfirmOpen(false);
    setCompanyToDelete(null);
  };

  const handleConfirmDeleteCompany = async () => {
    if (!companyToDelete) return;
    const company = companyToDelete;
    try {
      setLoading(true);
      await api.delete(`/companies/${company.id}/hard`);

      // Actualizar el estado local inmediatamente
      const updatedCompanies = companies.filter(c => c.id !== company.id);
      setCompanies(updatedCompanies);
      setFilteredCompanies(updatedCompanies);
      calculateStats(updatedCompanies);

      setError(null);
      setDeleteConfirmOpen(false);
      setCompanyToDelete(null);
      console.log(`✅ Empresa ${company.businessName} eliminada permanentemente`);
      console.log('📝 Evento de eliminación registrado automáticamente en el backend');
    } catch (err) {
      setError(`Error al eliminar la empresa: ${err.response?.data?.error || err.response?.data?.message || err.message}`);
      console.error('Error al eliminar empresa:', err);
      setDeleteConfirmOpen(false);
      setCompanyToDelete(null);
      // Recargar datos en caso de error para mantener consistencia
      fetchCompanies();
    } finally {
      setLoading(false);
    }
  };

  const handleViewBranches = (company) => {
    navigate(`/clientes/sucursales?companyId=${company.id}&companyName=${encodeURIComponent(company.businessName)}`);
  };

  const handleCreateDropboxStructure = async (company) => {
    if (window.confirm(`¿Desea crear la estructura de carpetas en Dropbox para la empresa "${company.businessName}"?`)) {
      try {
        setDropboxLoading(true);
        setError(null);
        setDropboxSuccess('');
        setDropboxWarning('');
        
        console.log('🏗️ Creando estructura Dropbox para empresa:', company.businessName);
        
        // Llamar al endpoint para crear estructura
        const response = await api.post(`/companies/${company.id}/dropbox/create-structure`);
        
        if (response.data.success) {
          setDropboxSuccess(`Estructura de Dropbox creada exitosamente para "${company.businessName}"`);
          console.log('✅ Estructura creada:', response.data);
          setTimeout(() => setDropboxSuccess(''), 6000);
        } else {
          // La estructura ya existe - mostrar como warning
          setDropboxWarning(`${response.data.message}`);
          console.log('⚠️ Respuesta:', response.data);
          setTimeout(() => setDropboxWarning(''), 8000);
        }
        
      } catch (err) {
        console.error('Error creando estructura Dropbox:', err);
        setError(`Error al crear estructura de Dropbox: ${err.response?.data?.message || err.message}`);
      } finally {
        setDropboxLoading(false);
      }
    }
  };

  const isEmpty = (val) => val === null || val === undefined || String(val).trim() === '';
  // Campos obligatorios: toda la etiqueta (no solo el asterisco) en un color tenue,
  // para distinguirlos de un vistazo sin competir visualmente con el rojo de "error".
  const REQUIRED_LABEL_COLOR = '#9c7c38';
  const requiredLabel = (text) => (
    <Box component="span" sx={{ color: REQUIRED_LABEL_COLOR }}>{text} *</Box>
  );

  const warnSx = (warn) => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      ...(warn && { '& fieldset': { borderColor: '#ed6c02' } }),
      '&:hover fieldset': { borderColor: warn ? '#ed6c02' : '#1976d2' },
    },
    '& .MuiInputLabel-root': { color: warn ? '#ed6c02' : '#000000' },
  });
  const warnSelectSx = (warn) => ({
    borderRadius: 2,
    ...(warn && { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ed6c02' } }),
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: warn ? '#ed6c02' : '#1976d2' },
  });

  // Igual que warnSx/warnSelectSx, pero además tiñe la caja (fondo + borde) con el mismo
  // tono tenue de REQUIRED_LABEL_COLOR cuando el campo es obligatorio -- para que se note
  // de un vistazo cuál hay que llenar sin que compita con el rojo de error ni el naranja
  // de "vacío en edición" (ese sigue ganando cuando aplica, warn tiene prioridad).
  const REQUIRED_FIELD_BG = '#fbf6ea';
  const REQUIRED_FIELD_BORDER = '#dcc48a';
  const requiredSx = (warn) => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: warn ? undefined : REQUIRED_FIELD_BG,
      '& fieldset': { borderColor: warn ? '#ed6c02' : REQUIRED_FIELD_BORDER },
      '&:hover fieldset': { borderColor: warn ? '#ed6c02' : REQUIRED_LABEL_COLOR },
    },
    '& .MuiInputLabel-root': { color: warn ? '#ed6c02' : '#000000' },
  });
  const requiredSelectSx = (warn) => ({
    borderRadius: 2,
    backgroundColor: warn ? undefined : REQUIRED_FIELD_BG,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: warn ? '#ed6c02' : REQUIRED_FIELD_BORDER },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: warn ? '#ed6c02' : REQUIRED_LABEL_COLOR },
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (loadErrorKind) {
    return (
      <Box p={3}>
        <ServerUnavailable kind={loadErrorKind} onRetry={() => {
          setLoadErrorKind(null);
          setError(null);
          setLoading(true);
          setReloadKey(k => k + 1);
        }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const sectionCardSx = {
    p: 0,
    mb: 3,
    borderRadius: 3,
    overflow: 'hidden',
    border: '1px solid #d9e2ec',
    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.06)',
    bgcolor: '#ffffff'
  };

  const sectionHeaderSx = {
    px: 3,
    py: 1.75,
    background: 'linear-gradient(90deg, #1b5eaa 0%, #1976d2 100%)',
    display: 'flex',
    alignItems: 'center',
    gap: 1.5
  };

  const sectionBodySx = {
    p: { xs: 2.5, md: 3 }
  };

  // Mismos rangos que Programas.jsx (edición de Datos del Inmueble desde Agenda de Servicios)
  // y NewQuotation.jsx -- es el mismo dato (companies.employees_count/construction_area/
  // land_area/construction_levels), así que debe validar igual sin importar desde qué pantalla
  // se edite, o se podía colar aquí lo que ya se bloquea en las otras dos.
  const PROPERTY_FIELD_LIMITS = {
    employeesCount:     { ...FIELD_LIMITS.employeesCount, label: 'empleados' },
    constructionArea:   { ...FIELD_LIMITS.constructionArea, label: 'm² de construcción' },
    landArea:           { ...FIELD_LIMITS.landArea, label: 'm² de terreno' },
    constructionLevels: { ...FIELD_LIMITS.constructionLevels, label: 'niveles' },
  };
  const propertyFieldHelperText = (field) => {
    const limits = PROPERTY_FIELD_LIMITS[field];
    return `Entre ${limits.min} y ${limits.max.toLocaleString('es-MX')} ${limits.label}`;
  };
  const handlePropertyFieldChange = (field, rawValue) => {
    const limits = PROPERTY_FIELD_LIMITS[field];
    const allowDecimal = field === 'constructionArea' || field === 'landArea';
    let sanitized = rawValue.replace(allowDecimal ? /[^0-9.]/g : /[^0-9]/g, '');
    if (allowDecimal) {
      // Solo un punto decimal
      const firstDot = sanitized.indexOf('.');
      if (firstDot !== -1) {
        sanitized = sanitized.slice(0, firstDot + 1) + sanitized.slice(firstDot + 1).replace(/\./g, '');
      }
    }
    // No dejar teclear más allá del máximo permitido
    if (sanitized !== '' && sanitized !== '.' && Number(sanitized) > limits.max) return;
    setFormData(prev => ({ ...prev, [field]: sanitized }));
  };

  // Completitud por sección para los chips del header del acordeón (visibles con la sección
  // colapsada). "Obligatorios" cuenta exactamente los campos marcados con * -- es decir, los que
  // usan requiredLabel(...), el mismo marcador visual que ya usa el formulario (incluye Estado y
  // Código Postal, que están marcados con * pero NO usan el helper requiredSx, así que un grep
  // por requiredSx solo los hubiera pasado por alto). "Total" cuenta todos los campos capturables
  // de la sección (obligatorios + opcionales), excluyendo toggles que no son "datos que faltan"
  // (Estado Activo/Inactivo, "misma póliza para todas las sucursales").
  const countFilled = (fields) => fields.filter(v => !isEmpty(v)).length;

  const generalRequiredFields = [formData.businessName];
  const generalAllFields = [formData.businessName, formData.legalName, formData.rfc, formData.businessSector];

  const contactoRequiredFields = [
    formData.corporateContactTitle, formData.corporateContactFirstName,
    formData.corporateContactPaternalSurname, formData.corporateContactEmail, formData.corporateContactPhone,
  ];
  const contactoAllFields = [
    ...contactoRequiredFields, formData.corporateContactMaternalSurname, formData.legalRepresentative,
    formData.insuranceCompany, formData.insurancePolicyNumber, formData.insurancePolicyValidity,
  ];

  const direccionFisicaRequiredFields = [
    formData.street, formData.exteriorNumber, formData.neighborhood, formData.municipality,
    formData.locality, formData.state, formData.postalCode,
  ];
  const direccionFisicaAllFields = [
    ...direccionFisicaRequiredFields, formData.interiorNumber, formData.geolocation,
    formData.betweenStreets, formData.reference,
  ];

  const direccionFiscalAllFields = [
    formData.fiscalStreet, formData.fiscalExteriorNumber, formData.fiscalInteriorNumber,
    formData.fiscalNeighborhood, formData.fiscalMunicipality, formData.fiscalState,
    formData.fiscalPostalCode, formData.fiscalCountry, formData.fiscalBetweenStreets, formData.fiscalReference,
  ];

  const datosInmuebleAllFields = [
    formData.employeesCount, formData.constructionArea, formData.landArea, formData.constructionLevels,
    formData.floatingPopulation, formData.maximumCapacity, formData.operatingHoursStart,
    formData.operatingHoursEnd, formData.administrativeHoursStart, formData.administrativeHoursEnd,
    formData.propertyRegime, formData.mainActivities,
  ];

  // Estilo base compartido: pill translúcida con borde cuando falta algo, relleno verde con
  // check cuando está completo -- misma idea visual para los 2 tipos de chip de header.
  const headerChipSx = (complete, pushRight) => ({
    ml: pushRight ? 'auto' : 0.75,
    mr: pushRight ? 0.75 : 1,
    height: 22,
    fontWeight: 700,
    fontSize: '0.7rem',
    letterSpacing: 0.2,
    color: 'white',
    border: '1px solid',
    borderColor: complete ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.55)',
    bgcolor: complete ? 'rgba(46,125,50,0.9)' : 'rgba(255,255,255,0.14)',
    boxShadow: complete ? '0 1px 4px rgba(0,0,0,0.25)' : 'none',
    transition: 'background-color 0.2s ease',
    '& .MuiChip-label': { px: 1 },
    '& .MuiChip-icon': { color: 'white', ml: '6px', fontSize: 14 },
  });

  const FieldsChip = ({ label, filled, total, pushRight }) => {
    const complete = filled === total;
    return (
      <Chip
        icon={complete ? <CheckCircleIcon /> : undefined}
        label={`${label} ${filled}/${total}`}
        size="small"
        sx={headerChipSx(complete, pushRight)}
      />
    );
  };

  const StatusChip = ({ done, doneLabel, pendingLabel, pushRight = true }) => (
    <Chip
      icon={done ? <CheckCircleIcon /> : undefined}
      label={done ? doneLabel : pendingLabel}
      size="small"
      sx={headerChipSx(done, pushRight)}
    />
  );

  return (
    <Box p={3}>
      <ReturnToValidationBanner info={returnToValidation} autoReturn onReturn={() => goBackToValidation(navigate, returnToValidation)} />
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
        Prospectos
      </Typography>

      {/* Mensajes de éxito, warning y error para Dropbox */}
      {dropboxSuccess && (
        <Alert 
          severity="success" 
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setDropboxSuccess('')}
        >
          {dropboxSuccess}
        </Alert>
      )}
      
      {dropboxWarning && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setDropboxWarning('')}
        >
          {dropboxWarning}
        </Alert>
      )}
      
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {/* Cards de Estadísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '120px', display: 'flex', alignItems: 'center' }}>
            <CardContent sx={{ textAlign: 'center', width: '100%' }}>
              <AssessmentIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                {stats.total}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Empresas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ bgcolor: '#e8f5e8', borderLeft: '4px solid #4caf50', height: '120px', display: 'flex', alignItems: 'center' }}>
            <CardContent sx={{ width: '100%', textAlign: 'center' }}>
              <Typography variant="h6" color="#4caf50">Activas</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.active}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ bgcolor: '#ffebee', borderLeft: '4px solid #f44336', height: '120px', display: 'flex', alignItems: 'center' }}>
            <CardContent sx={{ width: '100%', textAlign: 'center' }}>
              <Typography variant="h6" color="#f44336">Inactivas</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.inactive}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ bgcolor: '#fff3e0', borderLeft: '4px solid #ff9800', height: '120px', display: 'flex', alignItems: 'center' }}>
            <CardContent sx={{ width: '100%', textAlign: 'center' }}>
              <Typography variant="h6" color="#ff9800">Empresas Matriz</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.matriz}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabla de Empresas */}
      <Paper elevation={3}>
        <Box p={2}>
          {/* Header con título y botón de actualizar */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Listado de Empresas
            </Typography>
            <IconButton 
              onClick={fetchCompanies}
              sx={{ 
                bgcolor: 'primary.main',
                color: 'white',
                boxShadow: 2,
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'rotate(180deg)',
                  transition: 'all 0.3s ease-in-out'
                }
              }}
              title="Actualizar lista de empresas"
            >
              <RefreshIcon />
            </IconButton>
          </Box>
          
          {/* Botones de Acción */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setIsEditMode(false);
                clearForm();
                setDialogOpen(true);
              }}
              sx={{ textTransform: 'none' }}
              startIcon={<BusinessIcon />}
            >
              + Agregar Prospecto
            </Button>
          </Box>
          
          {/* Filtros */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <TextField
                fullWidth
                size="small"
                label="Filtrar por Nombre"
                value={filters.businessName}
                onChange={(e) => handleFilterChange('businessName', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <TextField
                fullWidth
                size="small"
                label="Filtrar por RFC"
                value={filters.rfc}
                onChange={(e) => handleFilterChange('rfc', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <TextField
                fullWidth
                size="small"
                label="Filtrar por Sector"
                value={filters.businessSector}
                onChange={(e) => handleFilterChange('businessSector', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={filters.companyType}
                  label="Tipo"
                  onChange={(e) => handleFilterChange('companyType', e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="MATRIZ">Matriz</MenuItem>
                  <MenuItem value="INDEPENDIENTE">Independiente</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select
                  value={filters.isActive}
                  label="Estado"
                  onChange={(e) => handleFilterChange('isActive', e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="true">Activas</MenuItem>
                  <MenuItem value="false">Inactivas</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Alert
          severity="info"
          icon={<EditIcon fontSize="small" />}
          sx={{ mx: 0, mb: 2, borderRadius: 2, '& .MuiAlert-message': { fontSize: '0.875rem' } }}
        >
          <strong>Tip:</strong> Haz doble click en cualquier fila para editar el prospecto directamente.
        </Alert>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortField === 'businessName'}
                    direction={sortField === 'businessName' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('businessName')}
                  >
                    Nombre Comercial
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortField === 'legalName'}
                    direction={sortField === 'legalName' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('legalName')}
                  >
                    Razón Social
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortField === 'rfc'}
                    direction={sortField === 'rfc' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('rfc')}
                  >
                    RFC
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Sector</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Contacto</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortField === 'createdByName'}
                    direction={sortField === 'createdByName' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('createdByName')}
                  >
                    Creado por
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortField === 'createdAt'}
                    direction={sortField === 'createdAt' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('createdAt')}
                  >
                    Creado el
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedCompanies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No se encontraron empresas
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedCompanies.map((company) => (
                  <TableRow 
                    key={company.id} 
                    hover
                    onDoubleClick={() => handleEditClick(company)}
                    title="Doble clic para editar"
                    sx={{ '&:hover': { bgcolor: '#f9f9f9' }, cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                        {company.businessName}
                      </Typography>
                    </TableCell>
                    <TableCell>{company.legalName || 'N/A'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {company.rfc}
                      </Typography>
                    </TableCell>
                    <TableCell>{company.businessSectorName || company.businessSector || 'N/A'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                        {company.corporateContactTitle && company.corporateContactFirstName ? 
                          `${company.corporateContactTitle} ${company.corporateContactFirstName} ${company.corporateContactPaternalSurname || ''} ${company.corporateContactMaternalSurname || ''}`.trim() 
                          : 'N/A'}
                      </Typography>
                      {company.corporateContactEmail && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {company.corporateContactEmail}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{company.createdByName || 'N/A'}</TableCell>
                    <TableCell>
                      {company.createdAt
                        ? new Date(company.createdAt).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small" 
                        onClick={(e) => handleActionMenuOpen(e, company)}
                        sx={{ color: '#666' }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Menú de Acciones */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            borderRadius: 2,
            minWidth: 200
          }
        }}
      >
        <MenuItem onClick={() => handleMenuAction('branches')}>
          <ListItemIcon>
            <AccountTreeIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText>Ver Sucursales</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('dropbox')} disabled={dropboxLoading}>
          <ListItemIcon>
            <CloudSyncIcon fontSize="small" color="secondary" />
          </ListItemIcon>
          <ListItemText>Crear Estructura Dropbox</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('documents')}>
          <ListItemIcon>
            <CloudUploadIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>Documentos de Cumplimiento</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('edit')}>
          <ListItemIcon>
            <EditIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>Editar</ListItemText>
        </MenuItem>
        {isAdmin && (
          <MenuItem onClick={() => handleMenuAction('delete')}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: '#d32f2f' }}>Eliminar</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Confirmación de eliminación permanente — solo admin */}
      <Dialog open={deleteConfirmOpen} onClose={handleCloseDeleteConfirm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#d32f2f', fontWeight: 700 }}>
          Eliminar empresa permanentemente
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Vas a eliminar <strong>{companyToDelete?.businessName}</strong> y TODA su información relacionada:
          </Typography>
          <Typography component="ul" sx={{ mb: 2, pl: 3 }}>
            <li>Todas sus cotizaciones (con órdenes de trabajo, evaluaciones de inspección, UIPC, capacitaciones, constancias, documentos y pagos)</li>
            <li>Todas sus sucursales</li>
            <li>Su usuario invitado (ya no podrá iniciar sesión)</li>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Los archivos ya subidos a Dropbox NO se eliminan.
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDeleteConfirm} disabled={loading}>Cancelar</Button>
          <Button onClick={handleConfirmDeleteCompany} variant="contained" color="error" disabled={loading}>
            {loading ? 'Eliminando...' : 'Eliminar definitivamente'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Documentos de Cumplimiento (matriz) */}
      <UploadDocumentosDialog
        open={documentsDialogOpen}
        onClose={() => setDocumentsDialogOpen(false)}
        company={selectedCompanyForDocs}
        onSuccess={() => setSnackbar({ open: true, message: 'Documento(s) subido(s) exitosamente', severity: 'success' })}
        onError={(msg) => setSnackbar({ open: true, message: msg, severity: 'error' })}
      />

      {/* Dialog Unificado para Crear/Editar Empresa */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseAttempt}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 24px 48px rgba(15, 23, 42, 0.16)',
            border: '1px solid #dbe4f0',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          textAlign: 'center',
          py: 3,
          borderBottom: '1px solid rgba(255,255,255,0.2)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 0.5 }}>
            <BusinessIcon sx={{ fontSize: 32 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              {isEditMode ? 'Editar Empresa' : 'Crear Nuevo Prospecto'}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Captura la información en bloques para una carga más clara y ordenada.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2.5, md: 4 }, bgcolor: '#f4f7fb' }}>
          {isEditMode && (
            <ReturnToValidationBanner info={returnToValidation} autoReturn sx={{ mb: 3 }}
              onReturn={() => goBackToValidation(navigate, returnToValidation)} />
          )}
          {isEditMode && (entityLock?.locked) && (
            <Alert severity={entityLock.canOverride || entityLock.editableWithNotice ? 'info' : 'warning'} sx={{ mb: 3, borderRadius: 2 }}>
              {entityLock.editableWithNotice ? (
                <>
                  <strong>Datos con los que se cotizó (empleados, m² y niveles):</strong> este registro {entityLock.reason}.
                  {' '}Puedes capturarlos o modificarlos; al guardar, el cambio queda anotado en la cotización y se avisa a
                  Ventas, Administración y Coordinación de Operaciones para que revisen si ajustan el costo.
                </>
              ) : (
                <>
                  <strong>Datos con los que se cotizó bloqueados (empleados, m² y niveles):</strong> este registro {entityLock.reason}.
                  {' '}Esos 4 datos no se pueden modificar hasta que se cierre, para que
                  la cotización, la orden de trabajo y los documentos coincidan.
                  {entityLock.canOverride && ' Como administrador sí puedes corregirlos.'}
                </>
              )}
            </Alert>
          )}
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3, 
                borderRadius: 2,
                '& .MuiAlert-icon': {
                  fontSize: 24
                }
              }}
            >
              {error}
            </Alert>
          )}

          <Alert
            severity="info"
            icon={false}
            sx={{
              mb: 3, borderRadius: 2, py: 1.25,
              border: `2px solid ${REQUIRED_FIELD_BORDER}`, bgcolor: REQUIRED_FIELD_BG,
              '& .MuiAlert-message': { width: '100%' }
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#5c4a1f' }}>
              Los campos marcados con{' '}
              <Box component="span" sx={{ color: REQUIRED_LABEL_COLOR, fontWeight: 800 }}>*</Box>
              {' '}y con la caja en{' '}
              <Box component="span" sx={{ color: REQUIRED_LABEL_COLOR, fontWeight: 800 }}>este tono dorado</Box>
              {' '}son obligatorios. El resto puede completarse posteriormente.
            </Typography>
          </Alert>
          
          {/* Sección de Información General */}
          <Accordion elevation={0} expanded={expandedSections.general} onChange={() => toggleSection('general')} sx={{ ...sectionCardSx, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />} sx={sectionHeaderSx}>
              <BusinessIcon sx={{ color: 'white', fontSize: 20 }} />
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem', m: 0 }}>
                Información General de la Empresa
              </Typography>
              {isEditMode && (
                <>
                  <FieldsChip label="Oblig." filled={countFilled(generalRequiredFields)} total={generalRequiredFields.length} pushRight />
                  <FieldsChip label="Total" filled={countFilled(generalAllFields)} total={generalAllFields.length} />
                </>
              )}
            </AccordionSummary>
            <AccordionDetails sx={sectionBodySx}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  id="field-NOMBRE_EMPRESA"
                  label={requiredLabel('Nombre Comercial')}
                  value={formData.businessName}
                  onChange={(e) => setFormData(prev => ({ ...prev, businessName: sanitizeBusinessName(e.target.value) }))}
                  inputProps={{ maxLength: FIELD_LIMITS.businessName }}
                  variant="outlined"
                  disabled={isEditMode}
                  error={fieldErrors.businessName}
                  helperText={fieldErrors.businessName ? 'Este campo es obligatorio' : 'Solo se permiten letras, números y los caracteres: . , - & /'}
                  sx={requiredSx(isEditMode && isEmpty(formData.businessName))}
                />
                {!isEditMode && (
                  <CaseTypoHint value={formData.businessName}
                    onFix={(fixed) => setFormData(prev => ({ ...prev, businessName: fixed }))} />
                )}
                {isEditMode && (
                  <Alert severity="warning" sx={{ mt: 1, fontSize: '0.85rem' }}>
                    <strong>⚠️ Advertencia:</strong> Una vez capturado, el nombre comercial no podrá modificarse. Este campo no se puede editar.
                  </Alert>
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  id="field-RAZON_SOCIAL"
                  label="Razón Social"
                  value={formData.legalName}
                  onChange={(e) => setFormData(prev => ({ ...prev, legalName: sanitizeBusinessName(e.target.value) }))}
                  inputProps={{ maxLength: FIELD_LIMITS.legalName }}
                  variant="outlined"
                  helperText="Solo se permiten letras, números y los caracteres: . , - & /"
                  sx={warnSx(isEditMode && isEmpty(formData.legalName))}
                />
                <CaseTypoHint value={formData.legalName}
                  onFix={(fixed) => setFormData(prev => ({ ...prev, legalName: fixed }))} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  id="field-RFC"
                  label="RFC"
                  value={formData.rfc}
                  onChange={handleRFCChange}
                  variant="outlined"
                  inputProps={{ maxLength: FIELD_LIMITS.rfc, style: { textTransform: 'uppercase' } }}
                  error={!rfcValidation.isValid || (!!validationError && validationError.includes('RFC'))}
                  helperText={
                    !rfcValidation.isValid 
                      ? rfcValidation.message 
                      : validationError && validationError.includes('RFC') 
                        ? validationError 
                        : rfcValidation.isValid && formData.rfc && rfcValidation.tipo 
                          ? `✓ ${rfcValidation.message}` 
                          : 'Ej: ABC123456XYZ (12-13 caracteres)'
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontFamily: 'monospace',
                      '&:hover fieldset': {
                        borderColor: !rfcValidation.isValid || (validationError && validationError.includes('RFC')) ? '#f44336' : rfcValidation.isValid && formData.rfc ? '#4caf50' : '#1976d2',
                      },
                      '& fieldset': {
                        borderColor: !rfcValidation.isValid || (validationError && validationError.includes('RFC')) ? '#f44336' : rfcValidation.isValid && formData.rfc ? '#4caf50' : '#e0e0e0',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: !rfcValidation.isValid || (validationError && validationError.includes('RFC')) ? '#f44336' : '#000000',
                    },
                    '& .MuiFormHelperText-root': {
                      minHeight: '1.25em',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: !rfcValidation.isValid || (validationError && validationError.includes('RFC')) ? '#f44336' : rfcValidation.isValid && formData.rfc ? '#4caf50' : '#666666',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 8, md: 8 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <FormControl fullWidth>
                    <InputLabel id="business-sector-label-edit" sx={{ color: '#000000' }}>
                      Sector Empresarial
                    </InputLabel>
                    <Select
                      labelId="business-sector-label-edit"
                      id="field-GIRO_EMPRESA"
                      value={formData.businessSector || ''}
                      label="Sector Empresarial"
                      onChange={(e) => setFormData(prev => ({ ...prev, businessSector: e.target.value }))}
                      sx={warnSelectSx(isEditMode && isEmpty(formData.businessSector))}
                    >
                      {businessSectors.map((sector) => (
                        <MenuItem key={sector.id} value={sector.id}>
                          {sector.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton
                    onClick={() => setSectorModalOpen(true)}
                    sx={{
                      mt: 0.5,
                      bgcolor: '#1976d2',
                      color: 'white',
                      '&:hover': {
                        bgcolor: '#1565c0',
                      },
                      width: 40,
                      height: 40,
                    }}
                    title="Agregar nuevo sector empresarial"
                  >
                    +
                  </IconButton>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={formData.isActive}
                    label="Estado"
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.value }))}
                    sx={{
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1976d2',
                      },
                    }}
                  >
                    <MenuItem value={true}>
                      <Chip label="Activa" color="success" size="small" />
                    </MenuItem>
                    <MenuItem value={false}>
                      <Chip label="Inactiva" color="error" size="small" />
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Sección de Contacto */}
          <Accordion elevation={0} expanded={expandedSections.contacto} onChange={() => toggleSection('contacto')} sx={{ ...sectionCardSx, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />} sx={sectionHeaderSx}>
              <PersonIcon sx={{ color: 'white', fontSize: 20 }} />
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem', m: 0 }}>
                Información de Contacto
              </Typography>
              {isEditMode && (
                <>
                  <FieldsChip label="Oblig." filled={countFilled(contactoRequiredFields)} total={contactoRequiredFields.length} pushRight />
                  <FieldsChip label="Total" filled={countFilled(contactoAllFields)} total={contactoAllFields.length} />
                </>
              )}
            </AccordionSummary>
            <AccordionDetails sx={sectionBodySx}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 2 }}>
                    <Autocomplete
                      freeSolo
                      fullWidth
                      options={COMMON_CONTACT_TITLES}
                      value={formData.corporateContactTitle}
                      onChange={(e, newValue) => setFormData(prev => ({ ...prev, corporateContactTitle: (newValue || '').slice(0, 5) }))}
                      onInputChange={(e, newValue) => setFormData(prev => ({ ...prev, corporateContactTitle: newValue.slice(0, 5) }))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={requiredLabel('Título')}
                          variant="outlined"
                          placeholder="Lic., Ing., Dr."
                          error={fieldErrors.corporateContactTitle}
                          helperText={fieldErrors.corporateContactTitle ? 'Este campo es obligatorio' : ''}
                          sx={requiredSx(isEditMode && isEmpty(formData.corporateContactTitle))}
                          inputProps={{ ...params.inputProps, maxLength: 5 }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      fullWidth
                      id="field-CONTACTO_CORPORATIVO"
                      label={requiredLabel('Nombre(s)')}
                      value={formData.corporateContactFirstName}
                      onChange={(e) => setFormData(prev => ({ ...prev, corporateContactFirstName: sanitizeTextInput(e.target.value, '.-') }))}
                      onBlur={(e) => e.target.value.trim() && setFormData(prev => ({ ...prev, corporateContactFirstName: capitalizePersonName(e.target.value) }))}
                      inputProps={{ maxLength: FIELD_LIMITS.personName }}
                      variant="outlined"
                      error={fieldErrors.corporateContactFirstName}
                      helperText={fieldErrors.corporateContactFirstName ? 'Este campo es obligatorio' : 'Solo se permiten letras, espacios y los caracteres: . -'}
                      sx={requiredSx(isEditMode && isEmpty(formData.corporateContactFirstName))}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3.5 }}>
                    <TextField
                      fullWidth
                      label={requiredLabel('Apellido Paterno')}
                      value={formData.corporateContactPaternalSurname}
                      onChange={(e) => setFormData(prev => ({ ...prev, corporateContactPaternalSurname: sanitizeTextInput(e.target.value, '.-') }))}
                      onBlur={(e) => e.target.value.trim() && setFormData(prev => ({ ...prev, corporateContactPaternalSurname: capitalizePersonName(e.target.value) }))}
                      inputProps={{ maxLength: FIELD_LIMITS.personName }}
                      variant="outlined"
                      error={fieldErrors.corporateContactPaternalSurname}
                      helperText={fieldErrors.corporateContactPaternalSurname ? 'Este campo es obligatorio' : 'Solo se permiten letras, espacios y los caracteres: . -'}
                      sx={requiredSx(isEditMode && isEmpty(formData.corporateContactPaternalSurname))}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3.5 }}>
                    <TextField
                      fullWidth
                      label="Apellido Materno"
                      value={formData.corporateContactMaternalSurname}
                      onChange={(e) => setFormData(prev => ({ ...prev, corporateContactMaternalSurname: sanitizeTextInput(e.target.value, '.-') }))}
                      onBlur={(e) => e.target.value.trim() && setFormData(prev => ({ ...prev, corporateContactMaternalSurname: capitalizePersonName(e.target.value) }))}
                      inputProps={{ maxLength: FIELD_LIMITS.personName }}
                      helperText="Solo se permiten letras, espacios y los caracteres: . -"
                      variant="outlined"
                      sx={warnSx(isEditMode && isEmpty(formData.corporateContactMaternalSurname))}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('Email Corporativo')}
                  type="email"
                  name="corporateContactEmail"
                  id="field-EMAIL_CORPORATIVO"
                  value={formData.corporateContactEmail}
                  onChange={(e) => handleEmailChange(sanitizeEmail(e.target.value))}
                  inputProps={{ maxLength: FIELD_LIMITS.email }}
                  variant="outlined"
                  error={fieldErrors.corporateContactEmail || !emailValidation.isValid || (!!validationError && (validationError.includes('email') || validationError.includes('correo')))}
                  helperText={
                    fieldErrors.corporateContactEmail
                      ? 'Este campo es obligatorio'
                      : !emailValidation.isValid
                        ? emailValidation.message
                        : validationError && (validationError.includes('email') || validationError.includes('correo'))
                          ? validationError
                          : emailValidation.isValid && formData.corporateContactEmail
                            ? emailValidation.message
                            : 'Formato: usuario@empresa.com'
                  }
                  sx={(() => {
                    const hasError = !emailValidation.isValid || (!!validationError && (validationError.includes('email') || validationError.includes('correo')));
                    const warn = isEditMode && isEmpty(formData.corporateContactEmail) && !hasError;
                    const isValidFilled = emailValidation.isValid && formData.corporateContactEmail;
                    return {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: (hasError || warn || isValidFilled) ? undefined : REQUIRED_FIELD_BG,
                        '&:hover fieldset': { borderColor: hasError ? '#f44336' : warn ? '#ed6c02' : '#1976d2' },
                        '& fieldset': { borderColor: hasError ? '#f44336' : warn ? '#ed6c02' : (isValidFilled ? '#4caf50' : REQUIRED_FIELD_BORDER) },
                      },
                      '& .MuiInputLabel-root': { color: hasError ? '#f44336' : warn ? '#ed6c02' : '#000000' },
                      '& .MuiFormHelperText-root': {
                        color: hasError ? '#f44336' : (isValidFilled ? '#4caf50' : '#666666'),
                      },
                    };
                  })()}
                />
                {isEditMode && !isEmpty(formData.corporateContactEmail) && (
                  <Button
                    size="small"
                    startIcon={<SendIcon />}
                    onClick={handleResendInvitation}
                    disabled={resendingInvitation}
                    sx={{ mt: 1, textTransform: 'none' }}
                  >
                    {resendingInvitation ? 'Reenviando...' : 'Reenviar invitación'}
                  </Button>
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  id="field-TELEFONO_CORPORATIVO"
                  label={requiredLabel('Teléfono Corporativo')}
                  value={formData.corporateContactPhone}
                  onChange={(e) => handleNumericChange('corporateContactPhone', e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.phone }}
                  variant="outlined"
                  error={fieldErrors.corporateContactPhone || !phoneValidation.isValid}
                  helperText={fieldErrors.corporateContactPhone ? phoneValidation.message : (!phoneValidation.isValid ? phoneValidation.message : 'Se requieren exactamente 10 dígitos numéricos')}
                  sx={requiredSx(isEditMode && isEmpty(formData.corporateContactPhone))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 12 }}>
                <TextField
                  fullWidth
                  label="Representante Legal"
                  value={formData.legalRepresentative}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ ...prev, legalRepresentative: sanitizePersonName(value) }));

                    // Validar en tiempo real
                    const validation = validateLegalRepresentativeFormat(value);
                    setLegalRepresentativeValidation(validation);
                  }}
                  onBlur={(e) => e.target.value.trim() && setFormData(prev => ({ ...prev, legalRepresentative: capitalizePersonName(e.target.value) }))}
                  inputProps={{ maxLength: FIELD_LIMITS.legalRepresentative }}
                  variant="outlined"
                  error={!legalRepresentativeValidation.isValid && !!formData.legalRepresentative?.trim()}
                  helperText={
                    !formData.legalRepresentative?.trim()
                      ? 'Opcional — puede completarse después. Solo se permiten letras, espacios, punto (.) y guion (-)'
                      : !legalRepresentativeValidation.isValid
                      ? legalRepresentativeValidation.message
                      : 'Solo se permiten letras, espacios, punto (.) y guion (-)'
                  }
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!formData.insuranceSameForBranches}
                      onChange={(e) => setFormData(prev => ({ ...prev, insuranceSameForBranches: e.target.checked }))}
                    />
                  }
                  label="Misma póliza de seguro para todas las sucursales"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  id="field-COMPANIA_SEGUROS"
                  label="Compañía de Seguros"
                  value={formData.insuranceCompany}
                  onChange={(e) => setFormData(prev => ({ ...prev, insuranceCompany: e.target.value }))}
                  inputProps={{ maxLength: FIELD_LIMITS.insuranceCompany }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  id="field-NUMERO_POLIZA"
                  label="Número de Póliza"
                  value={formData.insurancePolicyNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, insurancePolicyNumber: e.target.value }))}
                  inputProps={{ maxLength: FIELD_LIMITS.insurancePolicyNumber }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="date"
                  id="field-VIGENCIA_POLIZA"
                  label="Vigencia de la Póliza"
                  value={formData.insurancePolicyValidity}
                  onChange={(e) => setFormData(prev => ({ ...prev, insurancePolicyValidity: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
            </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Sección de Dirección */}
          <Accordion elevation={0} expanded={expandedSections.direccionFisica} onChange={() => toggleSection('direccionFisica')} sx={{ ...sectionCardSx, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />} sx={sectionHeaderSx}>
              <LocationIcon sx={{ color: 'white', fontSize: 20 }} />
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem', m: 0 }}>
                Dirección Física del Inmueble
              </Typography>
              {isEditMode && (
                <>
                  <FieldsChip label="Oblig." filled={countFilled(direccionFisicaRequiredFields)} total={direccionFisicaRequiredFields.length} pushRight />
                  <FieldsChip label="Total" filled={countFilled(direccionFisicaAllFields)} total={direccionFisicaAllFields.length} />
                </>
              )}
            </AccordionSummary>
            <AccordionDetails sx={sectionBodySx}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('Calle')}
                  value={formData.street || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, street: sanitizeAddress(e.target.value) }))}
                  inputProps={{ maxLength: FIELD_LIMITS.street }}
                  variant="outlined"
                  error={fieldErrors.street}
                  helperText={fieldErrors.street ? 'Este campo es obligatorio' : 'Solo se permiten letras, números y los caracteres: . , - # /'}
                  sx={requiredSx(isEditMode && isEmpty(formData.street))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 2, md: 3 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('Núm. Ext.')}
                  value={formData.exteriorNumber || ''}
                  onChange={(e) => handleNumericChange('exteriorNumber', e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.exteriorNumber }}
                  variant="outlined"
                  error={fieldErrors.exteriorNumber}
                  helperText={fieldErrors.exteriorNumber ? 'Este campo es obligatorio' : ''}
                  sx={requiredSx(isEditMode && isEmpty(formData.exteriorNumber))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 2 }}>
                <TextField
                  fullWidth
                  label="Núm. Int."
                  value={formData.interiorNumber || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, interiorNumber: e.target.value }))}
                  inputProps={{ maxLength: FIELD_LIMITS.interiorNumber }}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('Colonia')}
                  value={formData.neighborhood || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, neighborhood: sanitizeAddress(e.target.value) }))}
                  inputProps={{ maxLength: FIELD_LIMITS.neighborhood }}
                  variant="outlined"
                  error={fieldErrors.neighborhood}
                  helperText={fieldErrors.neighborhood ? 'Este campo es obligatorio' : 'Solo se permiten letras, números y los caracteres: . , - # /'}
                  sx={requiredSx(isEditMode && isEmpty(formData.neighborhood))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  id="field-MUNICIPIO"
                  label={requiredLabel('Delegación/Municipio')}
                  value={formData.municipality || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, municipality: sanitizeAddress(e.target.value) }))}
                  inputProps={{ maxLength: FIELD_LIMITS.municipality }}
                  variant="outlined"
                  error={fieldErrors.municipality}
                  helperText={fieldErrors.municipality ? 'Este campo es obligatorio' : 'Solo se permiten letras, números y los caracteres: . , - # /'}
                  sx={requiredSx(isEditMode && isEmpty(formData.municipality))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  id="field-LOCALIDAD"
                  label={requiredLabel('Localidad')}
                  value={formData.locality || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, locality: sanitizeAddress(e.target.value) }))}
                  inputProps={{ maxLength: FIELD_LIMITS.locality }}
                  variant="outlined"
                  error={fieldErrors.locality}
                  helperText={
                    fieldErrors.locality
                      ? 'Este campo es obligatorio'
                      : 'Requisito legal para la Carta de Corresponsabilidad -- puede ser distinta del municipio'
                  }
                  sx={requiredSx(isEditMode && isEmpty(formData.locality))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                <FormControl fullWidth error={fieldErrors.state}>
                  <InputLabel id="physical-state-label" shrink={!!formData.state} sx={{ color: '#000000' }}>
                    {requiredLabel('Estado')}
                  </InputLabel>
                  <Select
                    labelId="physical-state-label"
                    id="field-ESTADO"
                    value={formData.state || ''}
                    label="Estado *"
                    onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span style={{ color: '#999' }}>Seleccione un estado</span>;
                      }
                      return selected;
                    }}
                    sx={requiredSelectSx(isEditMode && isEmpty(formData.state))}
                  >
                    {MEXICO_STATES.map((st) => (
                      <MenuItem key={st} value={st}>
                        {st}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 3, md: 2 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('Código Postal')}
                  value={formData.postalCode || ''}
                  onChange={(e) => handlePostalCodeChange(e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.postalCode }}
                  variant="outlined"
                  error={!postalCodeValidation.isValid}
                  helperText={!postalCodeValidation.isValid ? "Código postal incorrecto" : "5 dígitos"}
                  sx={(() => {
                    const hasError = !postalCodeValidation.isValid;
                    const warn = isEditMode && isEmpty(formData.postalCode) && !hasError;
                    const borderColor = hasError ? '#f44336' : warn ? '#ed6c02' : REQUIRED_FIELD_BORDER;
                    const hoverColor = hasError ? '#f44336' : warn ? '#ed6c02' : REQUIRED_LABEL_COLOR;
                    return {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: (hasError || warn) ? undefined : REQUIRED_FIELD_BG,
                        '&:hover fieldset': { borderColor: hoverColor },
                        '& fieldset': { borderColor },
                      },
                      '& .MuiInputLabel-root': { color: hasError ? '#f44336' : warn ? '#ed6c02' : '#000000' },
                      '& .MuiFormHelperText-root': { color: hasError ? '#f44336' : '#666666' },
                    };
                  })()}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  id="field-GEOREFERENCIA"
                  label="Geolocalización (Lat,Lng)"
                  value={formData.geolocation || ''}
                  onChange={(e) => handleGeolocationChange(e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.geolocation }}
                  variant="outlined"
                  placeholder="Ejemplo: 19.4326,-99.1332"
                  error={!geolocationValidation.isValid}
                  helperText={!geolocationValidation.isValid ? geolocationValidation.message : ''}
                  sx={warnSx(isEditMode && isEmpty(formData.geolocation) && geolocationValidation.isValid)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  label="Entre Calles"
                  value={formData.betweenStreets || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, betweenStreets: sanitizeAddress(e.target.value) }))}
                  inputProps={{ maxLength: FIELD_LIMITS.betweenStreets }}
                  helperText="Solo se permiten letras, números y los caracteres: . , - # /"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  label="Referencia"
                  value={formData.reference || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, reference: sanitizeAddress(e.target.value) }))}
                  inputProps={{ maxLength: FIELD_LIMITS.reference }}
                  helperText="Solo se permiten letras, números y los caracteres: . , - # /"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
            </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Sección de Datos Fiscales */}
          <Accordion elevation={0} expanded={expandedSections.direccionFiscal} onChange={() => toggleSection('direccionFiscal')} sx={{ ...sectionCardSx, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />} sx={sectionHeaderSx}>
              <ReceiptIcon sx={{ color: 'white', fontSize: 20 }} />
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem', m: 0 }}>
                Dirección Fiscal (para facturación)
              </Typography>
              {isEditMode && <FieldsChip label="Total" filled={countFilled(direccionFiscalAllFields)} total={direccionFiscalAllFields.length} pushRight />}
            </AccordionSummary>
            <AccordionDetails sx={sectionBodySx}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <TextField
                  fullWidth
                  label="Calle Fiscal"
                  value={formData.fiscalStreet || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, fiscalStreet: sanitizeAddress(e.target.value) }))}
                  inputProps={{ maxLength: FIELD_LIMITS.street }}
                  variant="outlined"
                  helperText="Solo se permiten letras, números y los caracteres: . , - # /"
                  sx={warnSx(isEditMode && isEmpty(formData.fiscalStreet))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 2, md: 3 }}>
                <TextField
                  fullWidth
                  label="Núm. Ext."
                  value={formData.fiscalExteriorNumber || ''}
                  onChange={(e) => handleNumericChange('fiscalExteriorNumber', e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.exteriorNumber }}
                  variant="outlined"
                  helperText="Opcional"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 2, md: 3 }}>
                <TextField
                  fullWidth
                  label="Núm. Int."
                  value={formData.fiscalInteriorNumber || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, fiscalInteriorNumber: e.target.value }))}
                  inputProps={{ maxLength: FIELD_LIMITS.interiorNumber }}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  label="Colonia Fiscal"
                  value={formData.fiscalNeighborhood || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, fiscalNeighborhood: sanitizeAddress(e.target.value) }))}
                  inputProps={{ maxLength: FIELD_LIMITS.neighborhood }}
                  variant="outlined"
                  helperText="Solo se permiten letras, números y los caracteres: . , - # /"
                  sx={warnSx(isEditMode && isEmpty(formData.fiscalNeighborhood))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Delegación/Municipio Fiscal"
                  value={formData.fiscalMunicipality || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, fiscalMunicipality: sanitizeAddress(e.target.value) }))}
                  inputProps={{ maxLength: FIELD_LIMITS.municipality }}
                  variant="outlined"
                  helperText="Solo se permiten letras, números y los caracteres: . , - # /"
                  sx={warnSx(isEditMode && isEmpty(formData.fiscalMunicipality))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel id="fiscal-state-label" shrink={!!formData.fiscalState} sx={{ color: '#000000' }}>
                    Estado Fiscal
                  </InputLabel>
                  <Select
                    labelId="fiscal-state-label"
                    value={formData.fiscalState || ''}
                    label="Estado Fiscal"
                    onChange={(e) => setFormData(prev => ({ ...prev, fiscalState: e.target.value }))}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span style={{ color: '#999' }}>Seleccione un estado</span>;
                      }
                      return selected;
                    }}
                    sx={{
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1976d2',
                      },
                    }}
                  >
                    {MEXICO_STATES.map((st) => (
                      <MenuItem key={st} value={st}>
                        {st}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 2, md: 2 }}>
                <TextField
                  fullWidth
                  label="Código Postal Fiscal"
                  value={formData.fiscalPostalCode || ''}
                  onChange={(e) => handleFiscalPostalCodeChange(e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.postalCode }}
                  variant="outlined"
                  error={!fiscalPostalCodeValidation.isValid}
                  helperText={!fiscalPostalCodeValidation.isValid ? "Código postal incorrecto" : "5 dígitos"}
                  sx={(() => {
                    const hasError = !fiscalPostalCodeValidation.isValid;
                    const warn = isEditMode && isEmpty(formData.fiscalPostalCode) && !hasError;
                    const borderColor = hasError ? '#f44336' : warn ? '#ed6c02' : '#e0e0e0';
                    const hoverColor = hasError ? '#f44336' : warn ? '#ed6c02' : '#1976d2';
                    return {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': { borderColor: hoverColor },
                        '& fieldset': { borderColor },
                      },
                      '& .MuiInputLabel-root': { color: hasError ? '#f44336' : warn ? '#ed6c02' : '#000000' },
                      '& .MuiFormHelperText-root': { color: hasError ? '#f44336' : '#666666' },
                    };
                  })()}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 2, md: 2 }}>
                <TextField
                  fullWidth
                  label="País Fiscal"
                  value={formData.fiscalCountry || 'México'}
                  onChange={(e) => setFormData(prev => ({ ...prev, fiscalCountry: e.target.value }))}
                  inputProps={{ maxLength: FIELD_LIMITS.country }}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Entre Calles Fiscal"
                  value={formData.fiscalBetweenStreets || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, fiscalBetweenStreets: sanitizeAddress(e.target.value) }))}
                  inputProps={{ maxLength: FIELD_LIMITS.betweenStreets }}
                  variant="outlined"
                  helperText="Solo se permiten letras, números y los caracteres: . , - # /"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Referencia Fiscal"
                  value={formData.fiscalReference || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, fiscalReference: sanitizeAddress(e.target.value) }))}
                  inputProps={{ maxLength: FIELD_LIMITS.reference }}
                  variant="outlined"
                  helperText="Solo se permiten letras, números y los caracteres: . , - # /"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
            </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Sección de Datos del Inmueble (Opcional) -- header azul como las demás secciones; el
              aviso de "opcional pero requerido para algunos documentos" ya lo comunica el Alert
              de abajo, así que el naranja de alerta aquí ya no hace falta y solo alarmaba de más. */}
          <Accordion elevation={0} expanded={expandedSections.datosInmueble} onChange={() => toggleSection('datosInmueble')} sx={{ ...sectionCardSx, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />} sx={sectionHeaderSx}>
              <AssessmentIcon sx={{ color: 'white', fontSize: 20 }} />
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem', m: 0 }}>
                Datos del Inmueble
                {lockFields && <Chip size="small" label={`🔒 Bloqueado: ${entityLock.reason}`} sx={{ ml: 1, fontWeight: 500, bgcolor: '#fff3e0', color: '#e65100' }} />}
              </Typography>
              <Chip label="Opcional" size="small" sx={{ ml: 1, bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 'bold', fontSize: '0.7rem' }} />
              {isEditMode && <FieldsChip label="Total" filled={countFilled(datosInmuebleAllFields)} total={datosInmuebleAllFields.length} pushRight />}
            </AccordionSummary>
            <AccordionDetails sx={sectionBodySx}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Estos campos son opcionales. Puedes llenarlos ahora o completarlos más tarde, pero son requeridos para algunos documentos.
            </Alert>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  id="field-NUMERO_EMPLEADOS"
                  label="Número de Empleados"
                  disabled={lockFields}
                  value={formData.employeesCount || ''}
                  onChange={(e) => handlePropertyFieldChange('employeesCount', e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.employeesCount.digits }}
                  variant="outlined"
                  placeholder="Ej: 50"
                  helperText={propertyFieldHelperText('employeesCount')}
                  sx={warnSx(isEditMode && isEmpty(formData.employeesCount))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  id="field-AREA_CONSTRUCCION"
                  label="M² Construcción"
                  disabled={lockFields}
                  value={formData.constructionArea || ''}
                  inputProps={{ maxLength: FIELD_LIMITS.constructionArea.digits }}
                  onChange={(e) => handlePropertyFieldChange('constructionArea', e.target.value)}
                  variant="outlined"
                  placeholder="Ej: 500.50"
                  helperText={propertyFieldHelperText('constructionArea')}
                  sx={warnSx(isEditMode && isEmpty(formData.constructionArea))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  id="field-AREA_TERRENO"
                  label="M² Terreno"
                  disabled={lockFields}
                  value={formData.landArea || ''}
                  inputProps={{ maxLength: FIELD_LIMITS.landArea.digits }}
                  onChange={(e) => handlePropertyFieldChange('landArea', e.target.value)}
                  variant="outlined"
                  placeholder="Ej: 800.75"
                  helperText={propertyFieldHelperText('landArea')}
                  sx={warnSx(isEditMode && isEmpty(formData.landArea))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  id="field-NIVELES_CONSTRUCCION"
                  label="Niveles de Construcción"
                  disabled={lockFields}
                  value={formData.constructionLevels || ''}
                  onChange={(e) => handlePropertyFieldChange('constructionLevels', e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.constructionLevels.digits }}
                  variant="outlined"
                  placeholder="Ej: 3"
                  helperText={propertyFieldHelperText('constructionLevels')}
                  sx={warnSx(isEditMode && isEmpty(formData.constructionLevels))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  id="field-POBLACION_FLOTANTE"
                  label="Población Flotante"
                  value={formData.floatingPopulation || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    if (value.length <= 5) {
                      setFormData(prev => ({ ...prev, floatingPopulation: value }));
                    }
                  }}
                  variant="outlined"
                  placeholder="Ej: 50"
                  helperText="Opcional"
                  inputProps={{ maxLength: FIELD_LIMITS.floatingPopulation.digits }} />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  id="field-CAPACIDAD_MAXIMA"
                  label="Aforo Máximo"
                  value={formData.maximumCapacity || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    if (value.length <= 5) {
                      setFormData(prev => ({ ...prev, maximumCapacity: value }));
                    }
                  }}
                  variant="outlined"
                  placeholder="Ej: 50"
                  helperText="Máximo 5 dígitos"
                  inputProps={{ maxLength: FIELD_LIMITS.maximumCapacity.digits }} />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  type="time"
                  id="field-HORARIO_OPERATIVO"
                  label="Horario Operativo — Inicio"
                  value={formData.operatingHoursStart || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, operatingHoursStart: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  sx={warnSx(isEditMode && isEmpty(formData.operatingHoursStart))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  type="time"
                  label="Horario Operativo — Fin"
                  value={formData.operatingHoursEnd || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, operatingHoursEnd: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  sx={warnSx(isEditMode && isEmpty(formData.operatingHoursEnd))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  type="time"
                  id="field-HORARIO_ADMINISTRATIVO"
                  label="Horario Administrativo — Inicio"
                  value={formData.administrativeHoursStart || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, administrativeHoursStart: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  helperText="Opcional"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  type="time"
                  label="Horario Administrativo — Fin"
                  value={formData.administrativeHoursEnd || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, administrativeHoursEnd: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  helperText="Opcional"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#000000' }}>Régimen de Propiedad</InputLabel>
                  <Select
                    id="field-REGIMEN_PROPIEDAD"
                    value={formData.propertyRegime || ''}
                    label="Régimen de Propiedad"
                    onChange={(e) => setFormData(prev => ({ ...prev, propertyRegime: e.target.value }))}
                  >
                    <MenuItem value="">Sin especificar</MenuItem>
                    <MenuItem value="PROPIO">Propio</MenuItem>
                    <MenuItem value="ARRENDADO">Arrendado</MenuItem>
                    <MenuItem value="COMODATO">Comodato</MenuItem>
                    <MenuItem value="OTRO">Otro</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            {/* Fuera del Grid de columnas a propósito -- como Grid item nunca se veía a ancho
                completo (compartía fila con Régimen aunque tuviera xs=12), se renderiza como
                bloque aparte para garantizar el 100% del ancho sin depender del cálculo de
                columnas del Grid. */}
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <FormatListBulletedIcon sx={{ fontSize: 18, color: '#000000' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#000000' }}>
                  Actividades Principales
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>(Opcional)</Typography>
              </Box>
              <TextField
                fullWidth
                id="field-ACTIVIDADES_PRINCIPALES"
                value={formData.mainActivities || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, mainActivities: e.target.value }))}
                variant="outlined"
                multiline
                rows={4}
                placeholder="Ej: Venta al público de productos electrónicos, almacenamiento de mercancía en bodega trasera..."
                helperText={`${(formData.mainActivities || '').length}/500 caracteres`}
                inputProps={{ maxLength: FIELD_LIMITS.mainActivities }}
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': { borderRadius: 2 },
                }}
              />
            </Box>
            </AccordionDetails>
          </Accordion>

          {/* Sección de Logo de la Empresa — solo al editar una empresa existente */}
          {isEditMode && formData.id && (
            <Accordion id="field-LOGO_EMPRESA" elevation={0} expanded={expandedSections.logo} onChange={() => toggleSection('logo')} sx={{ ...sectionCardSx, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />} sx={sectionHeaderSx}>
                <CloudUploadIcon sx={{ color: 'white', fontSize: 20 }} />
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem', m: 0 }}>
                  Logo de la Empresa
                </Typography>
                <StatusChip done={companyHasLogo} doneLabel="Registrado" pendingLabel="Pendiente" />
              </AccordionSummary>
              <AccordionDetails sx={sectionBodySx}>
                {companyHasLogo && logoPreviewLoading && (
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Cargando previsualización del logo guardado…
                    </Typography>
                  </Box>
                )}

                {companyHasLogo && !logoPreviewLoading && !logoPreview && !logoError && (
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: '#43a047', fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">
                      Logo registrado. Puede actualizarlo subiendo un nuevo archivo.
                    </Typography>
                  </Box>
                )}

                {logoPreview && (
                  <Box sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 2, p: 1.5, display: 'inline-block' }}>
                    <img src={logoPreview} alt="Preview logo" style={{ height: 120, width: 'auto', maxWidth: 300, objectFit: 'contain' }} />
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept={JPG_PNG_ACCEPT}
                    style={{ display: 'none' }}
                    onChange={handleLogoSelect}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => logoInputRef.current?.click()}
                    sx={{ textTransform: 'none' }}
                  >
                    {logoFile ? logoFile.name : 'Seleccionar archivo .jpg o .png'}
                  </Button>
                  {logoFile && (
                    <Button
                      variant="contained"
                      onClick={handleLogoUpload}
                      disabled={logoUploading}
                      startIcon={logoUploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      {logoUploading ? 'Subiendo...' : 'Subir Logo'}
                    </Button>
                  )}
                </Box>

                {logoSuccess && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Logo subido correctamente.
                  </Alert>
                )}
                {logoError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {logoError}
                  </Alert>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                  Formato: JPG o PNG · Tamaño máximo: 5 MB
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Sección de Foto del Inmueble (matriz) — solo al editar una empresa existente */}
          {isEditMode && formData.id && (
            <Accordion id="field-IMAGEN_INMUEBLE" elevation={0} expanded={expandedSections.fotoInmueble} onChange={() => toggleSection('fotoInmueble')} sx={{ ...sectionCardSx, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />} sx={sectionHeaderSx}>
                <CloudUploadIcon sx={{ color: 'white', fontSize: 20 }} />
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem', m: 0 }}>
                  Fotografía del Inmueble (Matriz)
                </Typography>
                <StatusChip done={companyHasPropertyImage} doneLabel="Registrada" pendingLabel="Pendiente" />
              </AccordionSummary>
              <AccordionDetails sx={sectionBodySx}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Al subir el archivo se guarda de inmediato — no necesita presionar "Guardar" para que se registre.
                </Typography>
                {companyHasPropertyImage && propertyImagePreviewLoading && (
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Cargando previsualización de la fotografía guardada…
                    </Typography>
                  </Box>
                )}

                {companyHasPropertyImage && !propertyImagePreviewLoading && !propertyImagePreview && !propertyImageError && (
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: '#43a047', fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">
                      Fotografía registrada. Puede actualizarla subiendo un nuevo archivo.
                    </Typography>
                  </Box>
                )}

                {propertyImagePreview && (
                  <Box sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 2, p: 1.5, display: 'inline-block' }}>
                    <img src={propertyImagePreview} alt="Preview fotografía del inmueble" style={{ maxHeight: 160, maxWidth: 320, objectFit: 'contain' }} />
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    ref={propertyImageInputRef}
                    type="file"
                    accept={JPG_PNG_ACCEPT}
                    style={{ display: 'none' }}
                    onChange={handlePropertyImageSelect}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => propertyImageInputRef.current?.click()}
                    sx={{ textTransform: 'none' }}
                  >
                    {propertyImageFile ? propertyImageFile.name : 'Seleccionar archivo .jpg o .png'}
                  </Button>
                  {propertyImageFile && (
                    <Button
                      variant="contained"
                      onClick={handlePropertyImageUpload}
                      disabled={propertyImageUploading}
                      startIcon={propertyImageUploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      {propertyImageUploading ? 'Subiendo...' : 'Subir Fotografía'}
                    </Button>
                  )}
                </Box>

                {propertyImageSuccess && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Fotografía subida correctamente.
                  </Alert>
                )}
                {propertyImageError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {propertyImageError}
                  </Alert>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                  Formato: JPG o PNG · Tamaño máximo: 8 MB
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Sección de Constancia Fiscal — solo al editar una empresa existente. Es opcional
              (no bloquea nada si no se sube). Preview inline igual que logo/foto de inmueble,
              pero embebido en un <iframe> por ser PDF en vez de <img>. */}
          {isEditMode && formData.id && (
            <Accordion elevation={0} expanded={expandedSections.constanciaFiscal} onChange={() => toggleSection('constanciaFiscal')} sx={{ ...sectionCardSx, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />} sx={sectionHeaderSx}>
                <CloudUploadIcon sx={{ color: 'white', fontSize: 20 }} />
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem', m: 0 }}>
                  Constancia Fiscal
                </Typography>
                <StatusChip done={!!formData.constanciaFiscalPath} doneLabel="Registrada" pendingLabel="Opcional" />
              </AccordionSummary>
              <AccordionDetails sx={sectionBodySx}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Campo opcional — no es requerido para operar con la empresa.
                </Typography>

                {formData.constanciaFiscalPath && constanciaPreviewLoading && (
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Cargando previsualización de la constancia guardada…
                    </Typography>
                  </Box>
                )}

                {formData.constanciaFiscalPath && !constanciaPreviewLoading && !constanciaPreview && !constanciaError && (
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: '#43a047', fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">
                      Constancia registrada. Puede actualizarla subiendo un nuevo archivo.
                    </Typography>
                  </Box>
                )}

                {constanciaPreview && (
                  <Box sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 2, p: 1.5 }}>
                    <iframe
                      src={constanciaPreview}
                      title="Preview constancia fiscal"
                      style={{ width: '100%', height: 320, border: 'none', display: 'block' }}
                    />
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<CloudDownloadIcon fontSize="small" />}
                      onClick={handleConstanciaDownload}
                      disabled={constanciaActionLoading}
                      sx={{ textTransform: 'none', mt: 1 }}
                    >
                      Descargar
                    </Button>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    ref={constanciaInputRef}
                    type="file"
                    accept="application/pdf"
                    style={{ display: 'none' }}
                    onChange={handleConstanciaSelect}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => constanciaInputRef.current?.click()}
                    sx={{ textTransform: 'none' }}
                  >
                    {constanciaFile ? constanciaFile.name : 'Seleccionar archivo PDF'}
                  </Button>
                  {constanciaFile && (
                    <Button
                      variant="contained"
                      onClick={handleConstanciaUpload}
                      disabled={constanciaUploading}
                      startIcon={constanciaUploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      {constanciaUploading ? 'Subiendo...' : 'Subir Constancia'}
                    </Button>
                  )}
                </Box>

                {constanciaSuccess && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Constancia fiscal subida correctamente.
                  </Alert>
                )}
                {constanciaError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {constanciaError}
                  </Alert>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                  Formato: PDF · Tamaño máximo: 10 MB
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f8fbff', borderTop: '1px solid #dbe4f0', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          {/* Errores al guardar junto al botón (antes el genérico no se mostraba en ningún lado) */}
          {(validationError || error) && (
            <Alert severity="error" variant="filled" onClose={() => { setValidationError(''); setError(null); }} sx={{ flex: 1, mr: 'auto', borderRadius: 2 }}>
              {validationError || error}
            </Alert>
          )}
          <Button
            onClick={handleCloseAttempt}
            variant="outlined"
            sx={{
              color: '#666666',
              borderColor: '#cccccc',
              fontWeight: 'bold',
              minWidth: '160px',
              height: '48px',
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              '&:hover': {
                borderColor: '#999999',
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveClick}
            variant="contained"
            disabled={loading || logoUploading || propertyImageUploading || constanciaUploading}
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              color: 'white',
              fontWeight: 'bold',
              minWidth: '160px',
              height: '48px',
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
              },
              '&:disabled': {
                background: '#cccccc',
                color: '#666666'
              }
            }}
          >
            {loading ? 'Guardando...' : 
             isEditMode ? 'Guardar Cambios' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingUploadConfirm} onClose={() => setPendingUploadConfirm(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Tienes archivos seleccionados sin subir</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Elegiste estos archivos pero no les diste clic en "Subir". ¿Quieres subirlos junto con los cambios?
          </Typography>
          {pendingUploads.map(u => (
            <Typography key={u.label} variant="body2" sx={{ ml: 1 }}>
              • <strong>{u.label}:</strong> {u.file.name}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setPendingUploadConfirm(false)} sx={{ textTransform: 'none' }}>
            Volver
          </Button>
          <Button variant="outlined" sx={{ textTransform: 'none' }}
            onClick={() => { setPendingUploadConfirm(false); handleSubmit(); }}>
            Guardar sin subirlos
          </Button>
          <Button variant="contained" sx={{ textTransform: 'none' }}
            onClick={() => { setPendingUploadConfirm(false); handleSubmit({ uploadPending: true }); }}>
            Subir y guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Modal para agregar nuevo sector empresarial */}
      <AddBusinessSectorModal
        open={sectorModalOpen}
        onClose={() => setSectorModalOpen(false)}
        onSectorAdded={handleSectorAdded}
      />

      {/* Confirmación de descarte de datos */}
      <Dialog open={discardConfirmOpen} onClose={() => setDiscardConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
          ¿Descartar cambios?
        </DialogTitle>
        <DialogContent>
          <Typography>
            Tienes información ingresada en el formulario. Si cierras ahora, se perderán todos los datos.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setDiscardConfirmOpen(false)}
            variant="outlined"
            sx={{ textTransform: 'none', borderColor: '#cccccc', color: '#666666' }}
          >
            Seguir editando
          </Button>
          <Button
            onClick={() => {
              setDiscardConfirmOpen(false);
              setDialogOpen(false);
              setValidationError('');
              setIsEditMode(false);
              clearForm();
            }}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none' }}
          >
            Descartar
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default CatalogoEmpresas;
